import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Edit2, 
  Trash2, 
  FolderPlus, 
  Move,
  FileText,
  MessageCircle,
  FileDown,
  Menu,
  Share,
  Users
} from 'lucide-react';
import { useParams } from 'react-router-dom';
import { useCustomNavigate } from '../hooks/useCustomNavigate';
import { NavigationMenu } from '../components/NavigationMenu';
import { RichTextEditor } from '../components/RichTextEditor';
import { GoalViewer } from '../components/GoalViewer';
import { MoveDialog } from '../components/MoveDialog';
import { ChatHistory } from '../components/chat/ChatHistory';
import { ReelsList } from '../components/ReelsList';
import { knowledgeApi, KnowledgeItem, fetchContentPlan } from '../services/api';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { ChatMessage, ChatParticipant } from '../types/chat';
import { NewArticleDialog } from '../components/NewArticleDialog';
import { KnowledgeChat } from '../components/KnowledgeChat';
import { KnowledgeFileStructure } from '../components/KnowledgeFileStructure';
import { MobileKnowledgeOverlay } from '../components/MobileKnowledgeOverlay';
import { ContentPlanTable } from '../components/ContentPlanTable';
import { LoadingAnimation } from '../components/LoadingAnimation';
import { EditableTitle } from '../components/EditableTitle';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { AudienceAnalysisResultComponent } from '../components/AudienceAnalysis'; // <-- Добавлен импорт
import { SendMailDialog } from '../components/SendMailDialog';
import { DocumentParticipantsModal } from '../components/DocumentParticipantsModal';
import KanbanBoardStandalone from '../components/KanbanBoardStandalone';
// Убираем импорт конвертеров markdown, теперь Tiptap обрабатывает это сам
import { Plus } from 'lucide-react';
import { useKnowledgeTree, refreshKnowledgeTree, addDocumentToKnowledgeCache, removeDocumentFromKnowledgeCache } from '../hooks/useKnowledgeTree';
import { useBackgroundKnowledgeChat } from '../hooks/useBackgroundKnowledgeChat';
import { KnowledgeContext } from '../services/knowledgeChatService';
import { useFileRename } from '../hooks/useFileRename';

// --- НАЧАЛО: Типы для канбан ---
interface KanbanCard {
  id: string;
  title: string;
  description?: string;
  priority?: 'low' | 'medium' | 'high';
  deadline?: string;
  tags?: string[];
}

interface KanbanColumn {
  id: string;
  title: string;
  cardIds: string[];
  isCompletedColumn?: boolean;
}

interface BoardState {
  columns: KanbanColumn[];
  cards: { [key: string]: KanbanCard };
  columnOrder: string[];
  boardTitle: string;
}

// Интерфейсы для канбан данных (внутренний формат хранения)
interface KanbanBoard {
  id: string;
  title: string;
  columns: Array<{
    id: string;
    title: string;
    cards: KanbanCard[];
    isCompletedColumn?: boolean;
  }>;
}

interface KanbanData {
  boards: KanbanBoard[];
}
// --- КОНЕЦ: Типы для канбан ---

// Константы для localStorage
const LAST_OPENED_DOCUMENT_KEY = 'knowledge_last_opened_document';
const CHAT_STATES_KEY = 'knowledge_chat_states';

// Добавим объявление типа html2pdf для окна
declare global {
  interface Window {
    html2pdf: any;
  }
}

// Предохранитель для NavigationMenu, чтобы изолировать потенциальные ошибки контекста
const MemoizedNavigationMenu = React.memo(({ 
  onMenuStateChange, 
  isMenuActuallyOpen 
}: {
  onMenuStateChange: (isOpen: boolean) => void,
  isMenuActuallyOpen: boolean
}) => {
  try {
    return (
      <NavigationMenu 
        onMenuStateChange={onMenuStateChange} 
        isMenuActuallyOpen={isMenuActuallyOpen} 
      />
    );
  } catch (error) {
    console.error('Ошибка в NavigationMenu:', error);
    return null; // Запасной вариант на случай ошибки
  }
}, (prevProps, nextProps) => {
  // Кастомная функция сравнения - перерендериваем только при изменении пропсов меню
  return (
    prevProps.isMenuActuallyOpen === nextProps.isMenuActuallyOpen
    // Функции сравниваем по ссылке, они должны оставаться стабильными
    && prevProps.onMenuStateChange === nextProps.onMenuStateChange
  );
});

// Помощник для поиска элемента по ID в дереве
const findItemByIdRecursive = (items: KnowledgeItem[], id: string): KnowledgeItem | null => {
  for (const item of items) {
    if (item.id === id) {
      return item;
    }
    if (item.itemType === 'folder' && item.children) {
      const found = findItemByIdRecursive(item.children, id);
      if (found) return found;
    }
  }
  return null;
};

// Функция для удаления дублированных элементов по ID
const removeDuplicateItems = (items: KnowledgeItem[]): KnowledgeItem[] => {
  const seenIds = new Set<string>();
  
  const processItems = (items: KnowledgeItem[]): KnowledgeItem[] => {
    return items.filter(item => {
      if (seenIds.has(item.id)) {
        console.warn(`[Knowledge] Удаляем дублированный элемент с ID: ${item.id}, название: ${item.name}`);
        return false;
      }
      
      seenIds.add(item.id);
      
      // Рекурсивно обрабатываем дочерние элементы
      if (item.children) {
        item.children = processItems(item.children);
      }
      
      return true;
    });
  };
  
  return processItems(items);
};

// Мемоизируем весь компонент Knowledge для предотвращения лишних ререндеров
const Knowledge = React.memo(function Knowledge() {
  useDocumentTitle('База знаний');
  const navigate = useCustomNavigate();
  const { itemId } = useParams<{ itemId: string }>();
  const { data: queryItems = [], isFetching: isFetchingTree } = useKnowledgeTree();
  const queryClient = useQueryClient();
  
  // Инициализация CSS переменной
  useEffect(() => {
    // document.documentElement.style.setProperty(
    //   '--knowledge-panel-width', 
    //   isDesktop ? '300px' : '0px'
    // );
  }, []);
  const [items, setItems] = useState<KnowledgeItem[]>(queryItems);
  const [isLoading, setIsLoading] = useState(isFetchingTree);
  const [selectedItem, setSelectedItem] = useState<KnowledgeItem | null>(null);
  
  // Загрузка данных контент-плана через react-query
  const { data: contentPlanData } = useQuery({
    queryKey: ['content-plan', selectedItem?.id],
    queryFn: () => {
      return selectedItem?.id && !selectedItem.id.startsWith('temp-') 
        ? fetchContentPlan(selectedItem.id) 
        : Promise.resolve(null);
    },
    enabled: !!selectedItem?.id && !selectedItem.id.startsWith('temp-') && selectedItem.fileType === 'content-plan',
    staleTime: 5 * 60 * 1000, // 5 минут
    onSuccess: (data: any) => {
    },
    onError: (error: any) => {
    }
  });
  
  const [expandedFolders, setExpandedFolders] = useState<string[]>([]);
  const [isMenuOpenForContentShift, setIsMenuOpenForContentShift] = useState(false);
  const [isMobileOverlayOpen, setIsMobileOverlayOpen] = useState(false);
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; item: KnowledgeItem } | null>(null);
  const [isEditing, setIsEditing] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [moveDialogItem, setMoveDialogItem] = useState<KnowledgeItem | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<KnowledgeItem | null>(null);
  const [isEditingContent, setIsEditingContent] = useState(false);
  const [editableContent, setEditableContent] = useState('');
  const [showNewArticleDialog, setShowNewArticleDialog] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isChatFullscreen, setIsChatFullscreen] = useState(false);
  const [newArticleParentId, setNewArticleParentId] = useState<string | undefined>();
  const [modalPortalRoot, setModalPortalRoot] = useState<HTMLDivElement | null>(null);
  const [, setPreventLoadItems] = useState(false);
  const [, setOpenChats] = useState<KnowledgeItem['id'][]>([]);
  const [hasAutoOpenedMenu, setHasAutoOpenedMenu] = useState(false);
  const [hasCheckedForRestore, setHasCheckedForRestore] = useState(false);
  const [showSendMailDialog, setShowSendMailDialog] = useState(false);
  const [showParticipantsModal, setShowParticipantsModal] = useState(false);
  const [movingItems, setMovingItems] = useState<Set<string>>(new Set());
  const [moveError, setMoveError] = useState<string | null>(null);
  const [recentlyMovedItems, setRecentlyMovedItems] = useState<Map<string, number>>(new Map());

  // ИСПОЛЬЗУЕМ ТУ ЖЕ ЛОГИКУ, ЧТО И В DocumentParticipantsModal
  const hasParticipants = selectedItem?.isShared === true && 
    selectedItem?.participants && 
    Array.isArray(selectedItem.participants) && 
    selectedItem.participants.length > 1;
    



  const contextMenuRef = useRef<HTMLDivElement>(null);
  const deleteDialogRef = useRef<HTMLDivElement>(null);
  const autoSaveTimer = useRef<NodeJS.Timeout | null>(null);
  const prevSelectedItemIdRef = useRef<string | null>(null);
  
  // Защита от гонок при быстрой навигации между документами:
  // если ответы приходят не по порядку, игнорируем устаревшие.
  const itemLoadSeqRef = useRef(0);
  const latestRequestedItemIdRef = useRef<string | null>(null);
  
  // Отдельная защита для загрузки содержимого при клике по документу (handleSelectItem),
  // чтобы поздний ответ по старому клику не "перепрыгивал" текущий выбранный документ.
  const selectItemLoadSeqRef = useRef(0);
  const latestSelectRequestedItemIdRef = useRef<string | null>(null);

  // Если переключились на другой документ — сбрасываем режим редактирования и
  // гарантируем, что editableContent синхронизирован даже когда контент пустой ("").
  // Иначе может оставаться контент предыдущего документа.
  useEffect(() => {
    const currentId = selectedItem?.id ?? null;
    const prevId = prevSelectedItemIdRef.current;
    const idChanged = prevId !== currentId;
    
    if (idChanged) {
      setIsEditingContent(false);
      setEditableContent(selectedItem?.content ?? '');
      prevSelectedItemIdRef.current = currentId;
      return;
    }
    
    // Если это тот же документ и мы не редактируем — поддерживаем editableContent в актуальном состоянии.
    if (!isEditingContent) {
      setEditableContent(selectedItem?.content ?? '');
    }
  }, [selectedItem?.id, selectedItem?.content, isEditingContent]);

  // Хук для фоновой инициализации чатов
  const {
    initializeBackgroundChat,
    getBackgroundChatService,
    getPartialBackgroundChatService,
    cleanupBackgroundSession,
    cleanupAllBackgroundSessions,
    getCachedHistory
  } = useBackgroundKnowledgeChat();

  // Хук для управления переименованием файлов
  const {
    handleTitleInputChange: handleTitleInputChangeFromHook,
    handleTitleUpdate: handleTitleUpdateFromHook,
    cleanup: cleanupFileRename
  } = useFileRename({
    selectedItem,
    setItems,
    setSelectedItem
  });

  // Обработчик жеста свайпа слева направо для открытия меню
  const handleSwipeRight = (_event: any, info: any) => {
    const isMobile = window.innerWidth < 768; // md breakpoint
    if (!isMobile || isMobileOverlayOpen) return;

    const swipeThreshold = 50; // min distance in px
    const velocityThreshold = 100; // min velocity

    if (info.offset.x > swipeThreshold && info.velocity.x > velocityThreshold) {
      setIsMobileOverlayOpen(true);
    }
  };

  // Функция для сохранения состояния чата
  const saveChatState = (itemId: string, isOpen: boolean) => {
    try {
      const chatStates = JSON.parse(localStorage.getItem(CHAT_STATES_KEY) || '{}');
      chatStates[itemId] = isOpen;
      localStorage.setItem(CHAT_STATES_KEY, JSON.stringify(chatStates));
    } catch (error) {
      console.error('Ошибка при сохранении состояния чата:', error);
    }
  };

  // Функция для загрузки состояния чата
  const loadChatState = (itemId: string): boolean => {
    try {
      const chatStates = JSON.parse(localStorage.getItem(CHAT_STATES_KEY) || '{}');
      return chatStates[itemId] || false;
    } catch (error) {
      console.error('Ошибка при загрузке состояния чата:', error);
      return false;
    }
  };

  // Функция для переключения полноэкранного режима чата
  const handleToggleChatFullscreen = () => {
    setIsChatFullscreen(!isChatFullscreen);
  };

  // Функция для сохранения последнего открытого документа
  const saveLastOpenedDocument = (itemId: string | null) => {
    try {
      if (itemId && itemId !== 'root') {
        localStorage.setItem(LAST_OPENED_DOCUMENT_KEY, itemId);
      } else {
        localStorage.removeItem(LAST_OPENED_DOCUMENT_KEY);
      }
    } catch (error) {
      console.error('Ошибка при сохранении последнего документа:', error);
    }
  };

  // Функция для загрузки последнего открытого документа
  const loadLastOpenedDocument = (): string | null => {
    try {
      return localStorage.getItem(LAST_OPENED_DOCUMENT_KEY);
    } catch (error) {
      console.warn('Ошибка при загрузке последнего открытого документа:', error);
      return null;
    }
  };

  const memoizedSetItems = useCallback((newItems: KnowledgeItem[]) => {
    // Глубокое сравнение для предотвращения ненужных ререндеров
    if (JSON.stringify(items) !== JSON.stringify(newItems)) {
      setItems(newItems);
    }
  }, [items]); // Зависимость от 'items' для сравнения

  // Очищаем устаревшие элементы из recentlyMovedItems
  useEffect(() => {
    const now = Date.now();
    setRecentlyMovedItems(prev => {
      const newMap = new Map();
      for (const [id, timestamp] of prev) {
        if (now - timestamp < 5000) { // 5 секунд
          newMap.set(id, timestamp);
        }
      }
      return newMap;
    });
  }, [queryItems]); // Очищаем при каждом обновлении данных

  // Улучшенная синхронизация локального состояния с данными из React Query
  useEffect(() => {
    // Если у нас есть локальные изменения (временные элементы), НЕ перезаписываем их
    const hasLocalChanges = items.some(item => item.id.startsWith('temp-'));
    
    if (hasLocalChanges) {
      return;
    }
    
    // Если есть перемещаемые элементы, НЕ перезаписываем локальное состояние
    if (movingItems.size > 0) {
      return;
    }
    
    // Если есть недавно перемещенные элементы, НЕ перезаписываем локальное состояние
    if (recentlyMovedItems.size > 0) {
      return;
    }
    
    // Если данные с сервера еще загружаются, не синхронизируем
    if (isFetchingTree) {
      return;
    }
    
    
    // Проверяем, есть ли существенные различия между локальным состоянием и данными с сервера
    const hasSignificantChanges = JSON.stringify(items) !== JSON.stringify(queryItems);
    
    if (hasSignificantChanges) {
      
      // Удаляем дублированные элементы по ID перед синхронизацией
      const deduplicatedItems = removeDuplicateItems(queryItems);
      memoizedSetItems(deduplicatedItems);
    }
    
    setIsLoading(isFetchingTree);
  }, [queryItems, isFetchingTree, memoizedSetItems, items, movingItems, recentlyMovedItems]);

  // Сбрасываем флаг проверки при изменении URL или загрузке данных
  useEffect(() => {
    setHasCheckedForRestore(false);
  }, [itemId, isLoading]);

  // Эффект для восстановления последнего открытого документа при отсутствии itemId в URL
  useLayoutEffect(() => {
    // Если в URL нет itemId или itemId === 'root' (пользователь на /knowledge без конкретного документа)
    // и данные уже загружены, пытаемся восстановить последний открытый документ
    if ((!itemId || itemId === 'root') && items.length > 0 && !isLoading) {
      const lastOpenedId = loadLastOpenedDocument();
      
      if (lastOpenedId && lastOpenedId !== itemId) {
        const lastItem = findItemByIdRecursive(items, lastOpenedId);
        if (lastItem && lastItem.itemType === 'file') {
          setHasCheckedForRestore(true);
          navigate(`/knowledge/${lastOpenedId}`, { replace: true });
          return; // Выходим, чтобы дать сработать обычной логике обработки itemId
        } else {
          // Если сохраненный документ не найден, очищаем его из localStorage
          saveLastOpenedDocument(null);
        }
      }
      
      // Устанавливаем флаг, что проверка завершена
      setHasCheckedForRestore(true);
    }
  }, [itemId, items, isLoading, navigate]);

  // Единый эффект для управления меню
  useEffect(() => {
    const isMobile = window.innerWidth < 768; // md breakpoint  
    const isTablet = window.innerWidth >= 768 && window.innerWidth < 1024; // tablet breakpoint
    const isDesktop = window.innerWidth >= 1024; // lg breakpoint
    
    // На мобильных устройствах не перенаправляем - используем оверлей
    
    // Если данные еще не загружены или не завершена проверка восстановления, ничего не делаем
    if (items.length === 0 || !hasCheckedForRestore) {
      return;
    }
    
    // На desktop всегда показываем меню
    if (isDesktop && !isMenuOpenForContentShift) {
      setIsMenuOpenForContentShift(true);
      // document.documentElement.style.setProperty('--knowledge-panel-width', '300px');
      return;
    }
    
    // На мобильных устройствах всегда скрываем меню
    if (isMobile && isMenuOpenForContentShift) {
      setIsMenuOpenForContentShift(false);
    }
    
    if (!itemId || itemId === 'root') {
      // Общая база знаний - проверяем, есть ли сохранённый документ
      if (hasAutoOpenedMenu) {
        setHasAutoOpenedMenu(false);
      }
      
      if (isTablet && !isMenuOpenForContentShift) {
        // Для планшетов проверяем, будет ли восстановлен документ
        const lastOpenedId = loadLastOpenedDocument();
        const willRestoreDocument = lastOpenedId && findItemByIdRecursive(items, lastOpenedId);
        
        // Открываем меню только если НЕ будет восстановлен документ
        if (!willRestoreDocument) {
          console.log('[Knowledge] Открываем меню на планшете (нет документа для восстановления)');
          setTimeout(() => {
            setIsMenuOpenForContentShift(true);
          }, 100);
        } else {
          console.log('[Knowledge] НЕ открываем меню на планшете (будет восстановлен документ):', lastOpenedId);
        }
      }
    } else {
      // Конкретный документ - закрываем меню на мобильных и планшетах и ставим флаг
      if (!hasAutoOpenedMenu) {
        setHasAutoOpenedMenu(true);
      }
      
      // Закрываем меню для мобильных и планшетов при просмотре документа
      if ((isMobile || isTablet) && isMenuOpenForContentShift) {
        setIsMenuOpenForContentShift(false);
      }
    }
  }, [itemId, items.length, hasCheckedForRestore, isMenuOpenForContentShift]);

  // Добавляем эффект для обработки URL-параметра itemId
  useEffect(() => {

    // Если идет загрузка или нет элементов, ничего не делаем
    if (isLoading || items.length === 0) {
      return;
    }

    // Если текущий выбранный элемент временный, не позволяем этому useEffect его перезаписать,
    // пока URL не обновится на постоянный ID этого элемента (что обработает onIdChange).
    if (selectedItem && selectedItem.id.startsWith('temp-') && itemId !== selectedItem.id) {
      return;
    }

    // Дополнительная защита: если selectedItem уже соответствует itemId из URL,
    // и это изменение items вызвано обновлением кэша (а не пользовательской навигацией),
    // то не перезаписываем selectedItem
    if (selectedItem && itemId && selectedItem.id === itemId) {
      return;
    }

    if (itemId && items.length > 0) {
      if (itemId === "root") {
        // Проверяем, не равен ли уже selectedItem null, чтобы избежать лишних setState
        if (selectedItem !== null) {
          setSelectedItem(null);
          // Очищаем сохраненный документ при переходе в корень
          saveLastOpenedDocument(null);
        }
        return;
      }
      
      const item = findItemByIdRecursive(items, itemId); // <--- Используем новую функцию
      
      if (item) {
        // Если найденный элемент совпадает с текущим selectedItem (по ID), ничего не делаем, чтобы избежать лишних ререндеров.
        if (selectedItem && selectedItem.id === item.id) {
          // Однако, все равно нужно раскрыть родительские папки, если они еще не раскрыты
          const parentFolders = findParentFolders(items, itemId);
          let needsToUpdateExpanded = false;
          for (const folderId of parentFolders) {
            if (!expandedFolders.includes(folderId)) {
              needsToUpdateExpanded = true;
              break;
            }
          }
          if (needsToUpdateExpanded) {
            setExpandedFolders(prev => {
              const newExpanded = [...prev];
              for (const folderId of parentFolders) {
                if (!newExpanded.includes(folderId)) {
                  newExpanded.push(folderId);
                }
              }
              return newExpanded;
            });
          }
          return;
        }

        const parentFolders = findParentFolders(items, itemId);
        
        // Проверяем, нужно ли обновлять expandedFolders
        let needsToUpdateExpanded = false;
        for (const folderId of parentFolders) {
          if (!expandedFolders.includes(folderId)) {
            needsToUpdateExpanded = true;
            break;
          }
        }
        
        if (needsToUpdateExpanded) {
          setExpandedFolders(prev => {
            const newExpanded = [...prev];
            for (const folderId of parentFolders) {
              if (!newExpanded.includes(folderId)) {
                newExpanded.push(folderId);
              }
            }
            return newExpanded;
          });
        }
        
        // Устанавливаем selectedItem только если он действительно изменился
        if (!selectedItem || selectedItem.id !== item.id) {
          setSelectedItem(item);
          
          // Сохраняем открытый документ для восстановления
          if (item.itemType === 'file') {
            saveLastOpenedDocument(item.id);
          }
        }
      } else {
        // Если элемент по itemId не найден в items - принудительно обновляем кэш
        console.log(`[Knowledge useEffect<itemId,items>] Элемент с itemId: ${itemId} не найден в локальном кэше. Инвалидируем кэш для загрузки с сервера.`);
        
        // КРИТИЧНО: Инвалидируем кэш React Query для полной перезагрузки структуры с сервера
        // Это гарантирует, что мы получим актуальную и полную структуру файлов
        refreshKnowledgeTree();
        
        // Затем загружаем конкретный документ
        const loadSeq = ++itemLoadSeqRef.current;
        const requestedItemId = itemId;
        latestRequestedItemIdRef.current = requestedItemId;
        
        knowledgeApi.getFile(requestedItemId)
          .then(document => {
            // Если пользователь уже успел перейти на другой документ — игнорируем устаревший ответ.
            if (itemLoadSeqRef.current !== loadSeq || latestRequestedItemIdRef.current !== requestedItemId) {
              return;
            }
            console.log(`[Knowledge] Успешно загружен документ:`, document.name);
            
            // Добавляем документ в кэш React Query
            addDocumentToKnowledgeCache(document);
            
            // Устанавливаем его как выбранный
            setSelectedItem(document);
            
            // Сохраняем как последний открытый документ
            if (document.itemType === 'file') {
              saveLastOpenedDocument(document.id);
            }
            
            // Раскрываем родительские папки (используем queryItems которые обновятся после refreshKnowledgeTree)
            setTimeout(() => {
              // Если за время тайм-аута пользователь перешёл на другой документ — не трогаем UI.
              if (itemLoadSeqRef.current !== loadSeq || latestRequestedItemIdRef.current !== requestedItemId) {
                return;
              }
              const parentFolders = findParentFolders(queryItems, requestedItemId);
              if (parentFolders.length > 0) {
                setExpandedFolders(prev => {
                  const newExpanded = [...prev];
                  for (const folderId of parentFolders) {
                    if (!newExpanded.includes(folderId)) {
                      newExpanded.push(folderId);
                    }
                  }
                  return newExpanded;
                });
              }
            }, 300); // Даем время для обновления queryItems после refreshKnowledgeTree
          })
          .catch((error) => {
            // Не делаем редирект/ошибку, если запрос уже устарел из-за навигации.
            if (itemLoadSeqRef.current !== loadSeq || latestRequestedItemIdRef.current !== requestedItemId) {
              return;
            }
            console.error(`[Knowledge] Ошибка при загрузке документа ${itemId}:`, error);
            // При ошибке перенаправляем на главную страницу базы знаний
            navigate('/knowledge');
          });
      }
    } else if (!itemId && items.length > 0) {
      // Если itemId отсутствует (например, мы на /knowledge), но есть элементы, 
      // убедимся, что selectedItem сброшен, если он не был null.
      if (selectedItem !== null) {
        setSelectedItem(null);
        // Очищаем сохраненный документ
        saveLastOpenedDocument(null);
      }
    }
  }, [itemId, items, isLoading, navigate]);

  // Эффект для восстановления состояния чата при выборе документа
  useEffect(() => {
    if (selectedItem && selectedItem.itemType === 'file') {
      const savedChatState = loadChatState(selectedItem.id);
      setIsChatOpen(savedChatState);
    }
  }, [selectedItem]);

  // Эффект для сохранения состояния чата при его изменении
  useEffect(() => {
    if (selectedItem && selectedItem.itemType === 'file') {
      saveChatState(selectedItem.id, isChatOpen);
    }
  }, [isChatOpen, selectedItem]);

  // Эффект для сохранения состояния при закрытии страницы
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (selectedItem) {
        // Сохраняем текущее состояние перед закрытием
        if (selectedItem.itemType === 'file') {
          saveLastOpenedDocument(selectedItem.id);
          saveChatState(selectedItem.id, isChatOpen);
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [selectedItem, isChatOpen]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (contextMenuRef.current && !contextMenuRef.current.contains(event.target as Node)) {
        // Проверяем, не кликнули ли по кнопке меню или самому меню
        const hamburgerButton = document.querySelector('.menu-hamburger-button');
        const menuPanel = document.querySelector('.knowledge-panel');
        if (hamburgerButton && hamburgerButton.contains(event.target as Node)) {
          return; // Не закрываем контекстное меню при нажатии на кнопку гамбургера
        }
        if (menuPanel && menuPanel.contains(event.target as Node)) {
          return; // Не закрываем контекстное меню при нажатии внутри меню
        }
        setContextMenu(null);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (deleteDialogRef.current && !deleteDialogRef.current.contains(event.target as Node)) {
        setShowDeleteDialog(false);
        setItemToDelete(null);
      }
    }

    if (showDeleteDialog) {
      document.addEventListener('mousedown', handleClickOutside);
      
      // Фокусировка на диалоге удаления
      if (deleteDialogRef.current) {
        deleteDialogRef.current.focus();
      }
      
      // Добавляем обработчик клавиши Escape
      const handleEscape = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          setShowDeleteDialog(false);
          setItemToDelete(null);
        }
      };
      
      document.addEventListener('keydown', handleEscape);
      
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleEscape);
      };
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDeleteDialog]);

  // Создаем портал для модальных окон
  useEffect(() => {
    const portalRoot = document.createElement('div');
    portalRoot.id = 'modal-portal-root';
    document.body.appendChild(portalRoot);
    setModalPortalRoot(portalRoot);

    // Создаем и добавляем CSS для блокировки скролла
    const styleElement = document.createElement('style');
    styleElement.innerText = `
      body.modal-open {
        overflow: hidden !important;
        position: fixed !important;
        width: 100% !important;
        height: 100% !important;
        touch-action: none !important;
      }
      
      #modal-portal-root {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: 9999;
        pointer-events: none;
      }
      
      #modal-portal-root > * {
        pointer-events: auto;
      }
    `;
    document.head.appendChild(styleElement);

    return () => {
      document.body.removeChild(portalRoot);
      document.head.removeChild(styleElement);
    };
  }, []);

  // Устанавливаем класс для блокировки скролла
  useEffect(() => {
    const shouldLockScroll = showDeleteDialog || moveDialogItem !== null || showNewArticleDialog || contextMenu !== null;
    
    if (shouldLockScroll) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }
    
    return () => {
      document.body.classList.remove('modal-open');
    };
  }, [showDeleteDialog, moveDialogItem, showNewArticleDialog, contextMenu]);

  const toggleFolder = (id: string) => {
    setExpandedFolders(prev => {
      const isExpanded = prev.includes(id);
      if (isExpanded) {
        return prev.filter(folderId => folderId !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  // Рекурсивно находит все родительские папки для элемента
  const findParentFolders = (items: KnowledgeItem[], itemId: string, path: string[] = []): string[] => {
    for (const item of items) {
      if (item.id === itemId) {
        return path;
      }
      if (item.itemType === 'folder' && item.children) {
        const result = findParentFolders(item.children, itemId, [...path, item.id]);
        if (result.length > 0) {
          return result;
        }
      }
    }
    return [];
  };

  // Функция для подготовки элемента к добавлению дочерних элементов
  const prepareParentForChildren = (parentId: string) => {
    // Убедимся, что родительский элемент раскрыт
    setExpandedFolders(prev => {
      if (!prev.includes(parentId)) {
        return [...prev, parentId];
      }
      return prev;
    });
    
    // Убедимся, что у родительского элемента есть массив children
    setItems(prev => {
      const prepareParent = (items: KnowledgeItem[]): KnowledgeItem[] => {
        return items.map(item => {
          if (item.id === parentId) {
            return {
              ...item,
              children: item.children || []
            };
          }
          if (item.children) {
            return {
              ...item,
              children: prepareParent(item.children)
            };
          }
          return item;
        });
      };
      return prepareParent(prev);
    });
  };

  const handleNewArticle = async (type: string, parentId?: string) => {
    setShowNewArticleDialog(false);
    
    // Для файлов, которые создаются в Knowledge, создаем временный элемент для редактирования названия
    const tempId = `temp-${type}-${Date.now()}`;
    
    // Определяем стандартное имя в зависимости от типа
    let defaultName = 'Новый файл';
    if (type === 'empty') {
      defaultName = 'Новая статья';
    } else if (type === 'kanban') {
      defaultName = 'Новая канбан доска';
    }
    
    // Создаем временный файл с пустым содержимым для редактирования названия
    const tempFile: KnowledgeItem = {
      id: tempId,
      itemType: 'file',
      fileType: type === 'empty' ? 'article' : type,
      name: defaultName,
      content: '',
      parentId: parentId || null,
      metadata: undefined
    };
    
    // Добавляем временный файл в состояние
    setItems(prev => {
      if (!parentId) {
        return [...prev, tempFile];
      }

      const addToParent = (items: KnowledgeItem[]): KnowledgeItem[] => {
        return items.map(item => {
          if (item.id === parentId) {
            // Убедимся, что папка раскрыта
            if (!expandedFolders.includes(parentId)) {
              setExpandedFolders(prev => [...prev, parentId]);
            }
            return {
              ...item,
              children: [...(item.children || []), tempFile]
            };
          }
          if (item.children) {
            return {
              ...item,
              children: addToParent(item.children)
            };
          }
          return item;
        });
      };
      return addToParent(prev);
    });

    setSelectedItem(prevSelectedItem => {
      return tempFile;
    });
    
    // Сразу выбираем временный файл для отображения
    setSelectedItem(tempFile);
    
    // Активируем режим редактирования названия сначала
    setIsEditing(tempId);
    setEditName(defaultName);
  };





  const handleTaskToggle = async (index: number) => {
    if (!selectedItem) return;

    try {
      // Инициализируем metadata, если его нет
      const metadata = selectedItem.metadata || {};
      const completedTasks = metadata.completedTasks || [];
      const newCompletedTasks = completedTasks.includes(index)
        ? completedTasks.filter(i => i !== index)
        : [...completedTasks, index];

      const updatedItem = {
        ...selectedItem,
        metadata: {
          ...metadata,
          completedTasks: newCompletedTasks
        }
      };

      await knowledgeApi.updateItem(selectedItem.id, updatedItem);
      setSelectedItem(updatedItem);
    } catch (error) {
    }
  };

  // Функция для проверки и исправления временных parentId
  const checkAndFixTemporaryParentId = (item: KnowledgeItem): KnowledgeItem => {
    // Проверяем, что переданы корректные данные
    if (!item) {
      return item;
    }

    if (!item.parentId) {
      return item;
    }

    try {
      // Проверяем наличие временного parentId
      if (item.parentId.startsWith('temp-')) {
        
        // Попытка найти обновленный ID родительского элемента
        const findUpdatedParentId = (items: KnowledgeItem[], tempParentId: string): string | null => {
          // Проверяем, что массив элементов существует
          if (!items || !Array.isArray(items) || !tempParentId) {
            return null;
          }

          for (const i of items) {
            // Проверяем, что элемент определен и имеет id
            if (!i || !i.id) {
              continue;
            }

            // Если элемент имеет такой же "корень" временного ID, но уже имеет постоянный ID
            if (!i.id.startsWith('temp-') && tempParentId.includes('temp-file-') && i.id.includes(tempParentId.replace('temp-file-', ''))) {
              return i.id;
            }
            
            // Альтернативный подход: ищем элементы с тем же именем и типом, что могли бы быть родителем
            // Это более рискованный подход, но может помочь в некоторых сценариях
            const tempItem = items.find(ti => ti && ti.id === tempParentId);
            if (tempItem && !i.id.startsWith('temp-') && 
                i.name && tempItem.name && i.name === tempItem.name && 
                i.itemType && tempItem.itemType && i.itemType === tempItem.itemType) {
              return i.id;
            }
            
            // Рекурсивно проверяем дочерние элементы
            if (i.children && Array.isArray(i.children) && i.children.length > 0) {
              const foundId = findUpdatedParentId(i.children, tempParentId);
              if (foundId) return foundId;
            }
          }
          return null;
        };
        
        // Проверяем, что у нас есть массив items для поиска
        if (!items || !Array.isArray(items)) {
          return { ...item, parentId: null };
        }
        
        console.log('Ищем обновленный ID для временного parentId:', item.parentId);
        
        // Ищем обновленный ID родителя
        const updatedParentId = findUpdatedParentId(items, item.parentId);
        
        if (updatedParentId) {
          return { ...item, parentId: updatedParentId };
        } else {
          return { ...item, parentId: null };
        }
      }
      
      return item;
    } catch (error) {
      // Обрабатываем возможные ошибки
      return { ...item, parentId: null };
    }
  };

  const handleSaveContent = async (targetFolderId?: string | null) => {
    if (!selectedItem) return;

    // Проверяем и исправляем временный parentId
    let itemToSave = checkAndFixTemporaryParentId(selectedItem);
    
    const finalContent = isEditingContent ? editableContent : itemToSave.content || '';
    
    try {
      // Если targetFolderId определен, значит это новый файл или перемещение
      if (targetFolderId !== undefined) {
        const newItem: KnowledgeItem = {
          ...itemToSave,
          content: finalContent,
          parentId: targetFolderId
        };
        
        // Если ID начинается с "temp-", создаем новый файл
        if (selectedItem.id.startsWith('temp-')) {
          // Создаем новый файл через save
          const itemToSave = {
            ...newItem,
            id: ''
          };
          
          const createdItem = await knowledgeApi.save(itemToSave);
          
          // Проверяем, что у созданного файла есть ID
          if (!createdItem || !createdItem.id) {
            return;
          }
          
          // Если файл создается в папке, убедимся, что папка раскрыта
          if (targetFolderId) {
            setExpandedFolders(prev => {
              if (!prev.includes(targetFolderId)) {
                return [...prev, targetFolderId];
              }
              return prev;
            });
          }
          
          // ВАЖНОЕ ИСПРАВЛЕНИЕ: сохраняем контент в createdItem перед обновлением состояния
          const updatedCreatedItem = {
            ...createdItem,
            content: finalContent // Сохраняем исходный контент, который может отсутствовать в ответе сервера
          };
          
          // Обновляем список файлов, удаляя временный и добавляя новый
          setItems(prev => {
            const removeTemp = (items: KnowledgeItem[]): KnowledgeItem[] => {
              return items.filter(i => {
                if (i.id === selectedItem.id) return false;
                if (i.children) {
                  i.children = removeTemp(i.children);
                }
                return true;
              });
            };
            
            let newItems = removeTemp(prev);
            
            // Добавляем новый файл в нужную папку или в корень
            if (!targetFolderId) {
              newItems = [...newItems, updatedCreatedItem]; // Используем обновленный объект
            } else {
              const addToFolder = (items: KnowledgeItem[]): KnowledgeItem[] => {
                return items.map(i => {
                  if (i.id === targetFolderId) {
                    return {
                      ...i,
                      children: [...(i.children || []), updatedCreatedItem] // Используем обновленный объект
                    };
                  }
                  if (i.children) {
                    return { ...i, children: addToFolder(i.children) };
                  }
                  return i;
                });
              };
              newItems = addToFolder(newItems);
            }
            
            return newItems;
          });
          
          // Выбираем новый файл с сохраненным контентом
          setSelectedItem(updatedCreatedItem);
          // Обновляем и редактируемый контент
          setEditableContent(finalContent);
        } else {
          // Обновляем существующий файл
          const updatedItem = await knowledgeApi.updateItem(selectedItem.id, newItem);
          
          // Проверяем ответ от сервера
          if (!updatedItem) {
            return;
          }
          
          // ВАЖНОЕ ИСПРАВЛЕНИЕ: сохраняем контент в updatedItem перед обновлением состояния
          const updatedItemWithContent = {
            ...updatedItem,
            content: finalContent // Сохраняем исходный контент, который может отсутствовать в ответе сервера
          };
          
          // Обновляем элемент в списке файлов
          setItems(prevItems => {
            return updateItemContent(prevItems, selectedItem.id, finalContent);
          });
          
          setSelectedItem(updatedItemWithContent);
          // Обновляем и редактируемый контент
          setEditableContent(finalContent);
        }
      } else {
        // Просто обновляем содержимое существующего файла
        const updatedItem = {
          ...itemToSave,
          content: finalContent
        };
        const result = await knowledgeApi.updateItem(selectedItem.id, updatedItem);
        
        // Проверяем ответ от сервера
        if (!result) {
          return;
        }
        
        // ВАЖНОЕ ИСПРАВЛЕНИЕ: сохраняем контент в result перед обновлением состояния
        const resultWithContent = {
          ...result,
          content: finalContent // Сохраняем исходный контент, который может отсутствовать в ответе сервера
        };
        
        // Обновляем элемент в списке файлов
        setItems(prevItems => {
          return updateItemContent(prevItems, selectedItem.id, finalContent);
        });
        
        setSelectedItem(resultWithContent);
        // Обновляем и редактируемый контент
        setEditableContent(finalContent);
      }
      
      setIsEditingContent(false);
    } catch (error) {
    }
  };

  const toggleEditMode = () => {
    if (!isEditingContent) {
      // Подготавливаем контент для редактирования - Tiptap сам обработает markdown
      const contentForEditing = selectedItem?.content || '';
      setEditableContent(contentForEditing);
    } else {
      handleSaveContent();
    }
    setIsEditingContent(!isEditingContent);
  };

  const addNewFolder = (parentId?: string) => {
    // Генерируем временный ID для немедленного отображения
    const tempId = `temp-folder-${Date.now()}`;
    
    // Создаем новую папку с временным ID и именем по умолчанию
    const newFolder: KnowledgeItem = {
      id: tempId,
      itemType: 'folder',
      name: 'Новая папка', // Устанавливаем имя по умолчанию
      content: '',
      children: [],
      parentId: parentId || null,
      metadata: { isTemp: true } // Помечаем папку как временную
    };
    
    // Добавляем папку в состояние СРАЗУ с временным ID
    setItems(prevItems => {
      // Если есть родительская папка, добавляем в неё
      if (parentId) {
        // Функция для поиска и обновления родительской папки
        const addToParent = (items: KnowledgeItem[]): KnowledgeItem[] => {
          return items.map(item => {
            if (item.id === parentId) {
              // Добавляем новую папку в дочерние элементы родителя
              return {
                ...item,
                children: [...(item.children || []), newFolder]
              };
            }
            
            // Если у текущего элемента есть дочерние элементы, ищем в них
            if (item.children && item.children.length > 0) {
              return {
                ...item,
                children: addToParent(item.children)
              };
            }
            
            // Возвращаем элемент без изменений
            return item;
          });
        };
        
        return addToParent(prevItems);
      } else {
        // Если нет родительской папки, добавляем в корень
        return [...prevItems, newFolder];
      }
    });
    
    // Если добавляем в папку, раскрываем эту папку МГНОВЕННО
    if (parentId) {
      setExpandedFolders(prev => {
        if (!prev.includes(parentId)) {
          return [...prev, parentId];
        }
        return prev;
      });
    }
    
    // Включаем режим редактирования для новой папки МГНОВЕННО
    setSelectedItem(newFolder);
    setIsEditing(tempId); // Активируем режим редактирования по ID
    setEditName('Новая папка'); // Предзаполняем поле редактирования
    
    // Не выполняем никаких запросов на сервер - все изменения только локально
  };

  const handleSaveEdit = async (item: KnowledgeItem) => {
    console.log('[Knowledge] handleSaveEdit вызван для элемента:', {
      id: item.id,
      name: item.name,
      isTemporary: item.id.startsWith('temp-'),
      editName: editName
    });
    
    // Завершаем режим редактирования сразу
    setIsEditing(null);
    
    // Проверяем наличие ID у элемента
    if (!item || !item.id) {
      console.log('[Knowledge] Элемент не найден или нет ID');
      return;
    }

    // Получаем имя из поля редактирования
    const newName = editName.trim();
    console.log('[Knowledge] Новое имя:', newName);
    
    // Определяем тип элемента по ID
    const isNewItem = item.id.startsWith('temp-');
    const isFolder = item.itemType === 'folder';
    const isFile = item.itemType === 'file';
    
    console.log('[Knowledge] Тип элемента:', { isNewItem, isFolder, isFile });

    // Если имя пустое и это временный элемент, просто выходим из редактирования
    if (!newName && isNewItem) {
      console.log('[Knowledge] Временный элемент с пустым именем, выходим из редактирования');
      return;
    }
    
    // Если имя пустое для других элементов, просто выходим без изменений
    if (!newName) {
      console.log('[Knowledge] Имя пустое, выходим без изменений');
      return;
    }

    // СРАЗУ применяем изменения локально для мгновенного отклика UI
    const tempId = item.id;
    
    // Обновляем состояние элементов МГНОВЕННО
    setItems(prev => {
      const updateItemName = (items: KnowledgeItem[]): KnowledgeItem[] => {
        return items.map(i => {
          if (i.id === tempId) {
            return {
              ...i,
              name: newName
            };
          }
          if (i.children && i.children.length > 0) {
            return {
              ...i,
              children: updateItemName(i.children)
            };
          }
          return i;
        });
      };
      return updateItemName(prev);
    });
    
    // Обновляем имя выбранного элемента МГНОВЕННО
    if (selectedItem?.id === tempId) {
      setSelectedItem(prev => ({
        ...prev!,
        name: newName
      }));
    }

    // Теперь отправляем запросы на сервер В ФОНЕ (не блокируем UI)
    setTimeout(() => {
      try {
        console.log('[Knowledge] Начинаем обработку сохранения на сервере...');
        
        // Проверяем и исправляем временный parentId
        let itemToEdit = checkAndFixTemporaryParentId(item);
        
        // Если это новый элемент (с временным ID), создаем его через API
        if (isNewItem) {
          console.log('[Knowledge] Создаем новый элемент через API...');
          
          // Создаем объект для отправки через API
          const itemToCreate: KnowledgeItem = {
            id: '', // Будет присвоен сервером
            itemType: item.itemType,
            fileType: item.fileType,
            name: newName,
            content: item.fileType === 'kanban' ? JSON.stringify({
              boards: [{
                id: `board-${Date.now()}`,
                title: 'Основная доска',
                columns: [
                  { id: 'todo', title: 'К выполнению', cards: [] },
                  { id: 'in-progress', title: 'В работе', cards: [] },
                  { id: 'done', title: 'Выполнено', cards: [] }
                ]
              }]
            }) : '',
            parentId: itemToEdit.parentId,
            metadata: item.metadata
          };
          
          console.log('[Knowledge] Отправляем на сервер:', itemToCreate);
          
          // Отправляем запрос на сервер В ФОНЕ (не блокируем UI)
          knowledgeApi.save(itemToCreate)
            .then(response => {
              console.log('[Knowledge] Ответ от сервера:', response);
              
              if (!response || !response.id) {
                console.log('[Knowledge] Ошибка: нет ответа от сервера или нет ID');
                return;
              }
              
              // Создаем объект с правильным ID для замены временного
              const savedItem: KnowledgeItem = {
                ...itemToCreate,
                id: response.id
              };
              
              console.log('[Knowledge] Заменяем временный элемент на созданный:', savedItem);
              
              // Заменяем временный элемент на созданный
              setItems(prev => {
                const replaceTemp = (items: KnowledgeItem[]): KnowledgeItem[] => {
                  return items.map(i => {
                    if (i.id === item.id) {
                      return savedItem;
                    }
                    if (i.children) {
                      return {
                        ...i,
                        children: replaceTemp(i.children)
                      };
                    }
                    return i;
                  });
                };
                return replaceTemp(prev);
              });
              
              // Обновляем выбранный элемент, если он был временным
              if (selectedItem?.id === item.id) {
                setSelectedItem(savedItem);
              }
              
              console.log('[Knowledge] Временный элемент успешно заменен на созданный');
            })
            .catch(error => {
              console.error('[Knowledge] Ошибка при создании элемента:', error);
              
              // В случае ошибки удаляем временный элемент
              setItems(prev => {
                const removeTemp = (items: KnowledgeItem[]): KnowledgeItem[] => {
                  return items.filter(i => i.id !== item.id).map(i => {
                    if (i.children) {
                      return {
                        ...i,
                        children: removeTemp(i.children)
                      };
                    }
                    return i;
                  });
                };
                return removeTemp(prev);
              });
              
              // Если выбранный элемент был временным, сбрасываем выбор
              if (selectedItem?.id === item.id) {
                setSelectedItem(null);
              }
            });
        } else {
          // Для существующих элементов используем специализированный метод переименования
          console.log('[Knowledge] Переименовываем существующий элемент через API...');
          
          // Используем новый специализированный метод для переименования
          knowledgeApi.renameItem(itemToEdit.id, newName)
            .then(response => {
              console.log('[Knowledge] Элемент успешно переименован на сервере:', response);
            })
            .catch(error => {
              console.error('[Knowledge] Ошибка при переименовании элемента:', error);
              
              // В случае ошибки возвращаем прежнее имя
              setItems(prev => {
                const revertItemName = (items: KnowledgeItem[]): KnowledgeItem[] => {
                  return items.map(i => {
                    if (i.id === item.id) {
                      return {
                        ...i,
                        name: item.name
                      };
                    }
                    if (i.children && i.children.length > 0) {
                      return {
                        ...i,
                        children: revertItemName(i.children)
                      };
                    }
                    return i;
                  });
                };
                return revertItemName(prev);
              });
              
              // Возвращаем прежний заголовок выбранного элемента
              if (selectedItem?.id === item.id) {
                setSelectedItem(prev => ({
                  ...prev!,
                  name: item.name
                }));
              }
            });
        }
        
        console.log('[Knowledge] Обработка сохранения на сервере завершена');
        
      } catch (error) {
        console.error('[Knowledge] Ошибка в handleSaveEdit:', error);
      }
    }, 0); // Используем setTimeout с 0 задержкой для асинхронного выполнения
  };

  const handleMoveItem = async (targetFolderId: string | null) => {
    if (!moveDialogItem) return;
    
    // Сохраняем копию элемента для перемещения
    const itemToMove = { ...moveDialogItem };
    
    // Сразу закрываем диалог перемещения
    setMoveDialogItem(null);
    
    // Очищаем предыдущие ошибки
    setMoveError(null);
    
    // Добавляем элемент в список перемещаемых
    setMovingItems(prev => new Set(prev).add(itemToMove.id));
    
    // Если перемещаем в папку, убедимся, что она раскрыта
    if (targetFolderId) {
      setExpandedFolders(prev => {
        if (!prev.includes(targetFolderId)) {
          return [...prev, targetFolderId];
        }
        return prev;
      });
    }
    
    // Создаем копию элемента с обновленным parentId
    const updatedItem = {
      ...itemToMove,
      parentId: targetFolderId
    };
    
    // Сохраняем оригинальное состояние для возможного отката
    const originalItems = JSON.parse(JSON.stringify(items));
    
    // ОПТИМИСТИЧНОЕ ОБНОВЛЕНИЕ UI - обновляем состояние немедленно
    setItems(prev => {
      // Функция для удаления элемента из старого местоположения
      const removeFromOld = (items: KnowledgeItem[]): KnowledgeItem[] => {
        return items.filter(item => {
          if (item.id === itemToMove.id) {
            return false;
          }
          
          if (item.children) {
            item.children = removeFromOld(item.children);
          }
          
          return true;
        });
      };
      
      // Удаляем элемент из старого местоположения
      let newItems = removeFromOld(prev);
      
      // Функция для добавления элемента в новое местоположение
      const addToNew = (items: KnowledgeItem[]): KnowledgeItem[] => {
        if (!targetFolderId) {
          // Если перемещаем в корень
          return [...items, updatedItem];
        }
        
        return items.map(item => {
          if (item.id === targetFolderId) {
            return {
              ...item,
              children: [...(item.children || []), updatedItem]
            };
          }
          
          if (item.children) {
            return {
              ...item,
              children: addToNew(item.children)
            };
          }
          
          return item;
        });
      };
      
      // Добавляем элемент в новое местоположение
      return addToNew(newItems);
    });
    
    // Если элемент был выбран, обновляем его в выбранном состоянии
    if (selectedItem && selectedItem.id === itemToMove.id) {
      setSelectedItem(updatedItem);
    }
    
    // Отправляем запрос на перемещение в фоновом режиме
    try {
      await knowledgeApi.moveItem(itemToMove.id, targetFolderId, itemToMove.itemType);
      console.log('✅ [Knowledge] Файл успешно перемещен на сервере');
      
      // Обновляем кэш React Query для синхронизации с сервером
      queryClient.setQueryData(['knowledge-tree'], (oldData: KnowledgeItem[] | undefined) => {
        if (!oldData) return oldData;
        
        // Применяем те же изменения к кэшу React Query
        const removeFromOld = (items: KnowledgeItem[]): KnowledgeItem[] => {
          return items.filter(item => {
            if (item.id === itemToMove.id) {
              return false;
            }
            
            if (item.children) {
              item.children = removeFromOld(item.children);
            }
            
            return true;
          });
        };
        
        let newData = removeFromOld(oldData);
        
        const addToNew = (items: KnowledgeItem[]): KnowledgeItem[] => {
          if (!targetFolderId) {
            return [...items, updatedItem];
          }
          
          return items.map(item => {
            if (item.id === targetFolderId) {
              return {
                ...item,
                children: [...(item.children || []), updatedItem]
              };
            }
            
            if (item.children) {
              return {
                ...item,
                children: addToNew(item.children)
              };
            }
            
            return item;
          });
        };
        
        return addToNew(newData);
      });
      
      // Успешное перемещение завершено - добавляем в список недавно перемещенных
      console.log('✅ [Knowledge] Перемещение завершено успешно, добавляем в список недавно перемещенных');
      
      // Добавляем элемент в список недавно перемещенных с текущим временем
      setRecentlyMovedItems(prev => {
        const newMap = new Map(prev);
        newMap.set(itemToMove.id, Date.now());
        return newMap;
      });
      
      // Убираем элемент из списка перемещаемых
      setMovingItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(itemToMove.id);
        return newSet;
      });
      
    } catch (error) {
      console.error('❌ [Knowledge] Ошибка при перемещении файла:', error);
      
      // ОТКАТ ИЗМЕНЕНИЙ при ошибке
      setItems(originalItems);
      
      // Восстанавливаем выбранный элемент если он был изменен
      if (selectedItem && selectedItem.id === itemToMove.id) {
        setSelectedItem(itemToMove);
      }
      
      // Определяем тип ошибки и формируем сообщение
      let errorMessage = 'Неизвестная ошибка при перемещении файла';
      
      if (error instanceof Error) {
        if (error.message.includes('ERR_CONNECTION_RESET') || error.message.includes('net::ERR_CONNECTION_RESET')) {
          errorMessage = 'Соединение с сервером прервано. Попробуйте еще раз.';
        } else if (error.message.includes('timeout')) {
          errorMessage = 'Превышено время ожидания. Попробуйте еще раз.';
        } else if (error.message.includes('Network Error')) {
          errorMessage = 'Ошибка сети. Проверьте подключение к интернету.';
        } else {
          errorMessage = error.message;
        }
      }
      
      setMoveError(`Ошибка при перемещении "${itemToMove.name}": ${errorMessage}`);
      
      // Автоматически скрываем ошибку через 7 секунд для сетевых ошибок
      setTimeout(() => {
        setMoveError(null);
      }, 7000);
      
    } finally {
      // Убираем элемент из списка перемещаемых в любом случае
      setMovingItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(itemToMove.id);
        return newSet;
      });
    }
  };

  const handleContextMenu = (e: React.MouseEvent, item: KnowledgeItem) => {
    e.preventDefault();
    
    // Определяем размеры экрана и положение меню
    const windowHeight = window.innerHeight;
    const windowWidth = window.innerWidth;
    const menuHeight = 220; // Примерная высота меню
    const menuWidth = 180;  // Примерная ширина меню
    
    // Если меню выходит за нижнюю границу экрана, поднимаем его выше
    const yPosition = e.clientY + menuHeight > windowHeight 
      ? windowHeight - menuHeight - 10 // Отступ 10px от низа экрана
      : e.clientY;
    
    // Если меню выходит за правую границу экрана, смещаем его влево
    const xPosition = e.clientX + menuWidth > windowWidth
      ? windowWidth - menuWidth - 10 // Отступ 10px от правого края
      : e.clientX;
    
    setContextMenu({
      x: xPosition,
      y: yPosition,
      item
    });
  };

  const handleEdit = (item: KnowledgeItem) => {
    if (item.fileType === 'roadmap') {
    }
    setIsEditing(item.id);
    setEditName(item.name);
    setContextMenu(null);
  };

  const handleDelete = (item: KnowledgeItem) => {
    if (item.fileType === 'roadmap') {
    }
    setItemToDelete(item);
    setShowDeleteDialog(true);
    setContextMenu(null);
  };

  const handleMove = (item: KnowledgeItem) => {
    setMoveDialogItem(item);
    setContextMenu(null);
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;
    
    // Добавляем лог для roadmap
    if (itemToDelete.fileType === 'roadmap') {
    }
    
    // Сохраняем копию элемента для удаления
    const itemToDeleteCopy = { ...itemToDelete };
    
    // Сразу закрываем диалог и очищаем состояние
    setShowDeleteDialog(false);
    setItemToDelete(null);
    
    // Инвалидируем любые текущие/ожидающиеся загрузки, чтобы поздние ответы
    // не могли снова выставить selectedItem (особенно если удаляем документы подряд).
    itemLoadSeqRef.current += 1;
    latestRequestedItemIdRef.current = null;
    selectItemLoadSeqRef.current += 1;
    latestSelectRequestedItemIdRef.current = null;
    
    // Если удаляемый элемент был выбран, сбрасываем выбор МГНОВЕННО
    if (selectedItem && selectedItem.id === itemToDeleteCopy.id) {
      setSelectedItem(null);
    }
    
    // Если удаляем текущий документ из URL — сразу уходим на /knowledge,
    // иначе эффект обработки itemId может попытаться его подгрузить и выбрать снова.
    if (itemId === itemToDeleteCopy.id) {
      saveLastOpenedDocument(null);
      navigate('/knowledge', { replace: true });
    }
    
    // КРИТИЧНО: Обновляем кэш React Query НЕМЕДЛЕННО
    removeDocumentFromKnowledgeCache(itemToDeleteCopy.id);
    
    // Обновляем локальное состояние UI МГНОВЕННО, не дожидаясь ответа от сервера
    setItems(prev => {
      const removeItem = (items: KnowledgeItem[]): KnowledgeItem[] => {
        return items.filter(item => {
          if (item.id === itemToDeleteCopy.id) {
            return false;
          }
          
          if (item.children) {
            item.children = removeItem(item.children);
          }
          
          return true;
        });
      };
      
      return removeItem(prev);
    });
    
    // Отправляем запрос на удаление В ФОНЕ (не блокируем UI)
    knowledgeApi.deleteItem(itemToDeleteCopy.id)
      .then(() => {
        // Инвалидируем кэш для гарантированной синхронизации с сервером
        refreshKnowledgeTree();
      })
      .catch(error => {
        // При ошибке восстанавливаем элемент через полную перезагрузку дерева
        refreshKnowledgeTree();
      });
  };

  const parseMessages = (content: string): ChatMessage[] => {
    const messages: ChatMessage[] = [];
    const lines = content.split('\n');
    let currentSender: ChatParticipant | null = null;
    let currentContent = '';

    lines.forEach(line => {
      if (line.startsWith('**') && line.includes('**:')) {
        if (currentSender && currentContent.trim()) {
          messages.push({
            id: messages.length.toString(),
            sender: currentSender,
            content: currentContent.trim(),
            timestamp: new Date()
          });
          currentContent = '';
        }

        const senderName = line.replace(/^\*\*(.*?)\*\*:.*$/, '$1').trim();
        currentSender = {
          id: senderName.toLowerCase().replace(/\s+/g, '_'),
          name: senderName,
          role: senderName === 'Пользователь' ? 'Пользователь' : 'Эксперт',
          isOnline: false
        };
        currentContent = line.replace(/^\*\*(.*?)\*\*:\s*/, '') + '\n';
      } else if (currentSender) {
        currentContent += line + '\n';
      }
    });

    if (currentSender && currentContent.trim()) {
      messages.push({
        id: messages.length.toString(),
        sender: currentSender,
        content: currentContent.trim(),
        timestamp: new Date()
      });
    }

    return messages;
  };

  const parseReels = (content: string | undefined): any[] => {
    if (!content) {
      return [];
    }
    
    try {
      const parsedData = JSON.parse(content);
      return parsedData;
    } catch (error) {
      return [];
    }
  };

  const parseContentPlan = (content: string | undefined): any => {
    if (!content) return null;
    try {
      return JSON.parse(content);
    } catch (error) {
      return null;
    }
  };

  const handleSelectItem = async (item: KnowledgeItem) => {
    if (!item) {
      return;
    }

    // Проверяем, является ли элемент временным
    const isTemporary = item.id.startsWith('temp-');

    // Проверяем и исправляем временный parentId
    const itemWithFixedParent = checkAndFixTemporaryParentId(item);

    // Для папок - только раскрываем/скрываем, НЕ выбираем
    if (itemWithFixedParent.itemType === 'folder') {
      console.log('[Knowledge] Клик по папке, только переключаем состояние:', itemWithFixedParent.id);
      toggleFolder(itemWithFixedParent.id);
      return; // Выходим, не устанавливаем selectedItem
    }

    // Обновляем URL при выборе элемента, если это не папка и не временный файл
    if (itemWithFixedParent.itemType === 'file' && !isTemporary) {
      navigate(`/knowledge/${itemWithFixedParent.id}`);
      // Сохраняем выбранный документ для восстановления
      saveLastOpenedDocument(itemWithFixedParent.id);
      // Прокручиваем страницу вверх при выборе нового файла
      window.scrollTo(0, 0);
    }

    // Дополнительная проверка - если ID временный, ищем возможное обновление ID в состоянии
    if (isTemporary) {
      // Для временных элементов НЕ устанавливаем selectedItem
      // Пользователь должен сначала ввести название
      console.log('Выбран временный элемент:', itemWithFixedParent.id);
      
      // Проверяем, не находится ли элемент в режиме редактирования
      if (isEditing === itemWithFixedParent.id) {
        // Если элемент в режиме редактирования, просто выходим
        return;
      }
      
      // НЕ устанавливаем selectedItem для временных элементов
      // НЕ подготавливаем содержимое
      return;
    }
    
    // Если выбран элемент типа файл и НЕ временный, проверяем необходимость загрузки
    if (itemWithFixedParent.itemType === 'file') {
      try {
        const loadSeq = ++selectItemLoadSeqRef.current;
        const requestedId = itemWithFixedParent.id;
        latestSelectRequestedItemIdRef.current = requestedId;
        
        // Проверяем, совпадает ли текущий выбранный элемент с выбранным
        if (selectedItem?.id === itemWithFixedParent.id) {
          // Если это тот же файл и у нас есть его содержимое, пропускаем загрузку
          if (selectedItem.content !== undefined) {
            return;
          }
        }

        console.log('Загружаем файл по ID:', itemWithFixedParent.id);
        // Загружаем полное содержимое файла с сервера
        const fullItem = await knowledgeApi.getFile(requestedId);
        
        // Пользователь мог уже выбрать другой документ — игнорируем устаревший ответ.
        if (selectItemLoadSeqRef.current !== loadSeq || latestSelectRequestedItemIdRef.current !== requestedId) {
          return;
        }
        
        // Только если элемент успешно загружен с сервера
        if (fullItem && fullItem.id) {
          setSelectedItem(fullItem);
          
          // Подготавливаем содержимое для editableContent - Tiptap сам обработает markdown
          const contentForEditing = fullItem.content || '';
          setEditableContent(contentForEditing);
          
          // Инициализируем фоновый чат для загруженного документа
          if (fullItem.itemType === 'file') {
            const knowledgeContext: KnowledgeContext = {
              title: fullItem.name,
              itemType: 'document', 
              content: fullItem.content || ''
            };
            
            // Запускаем фоновую инициализацию асинхронно
            initializeBackgroundChat(fullItem.id, knowledgeContext)
              .then(() => {
              })
              .catch(error => {
              });
          }
          
          // Если это файл статьи, проверим необходимость активации редактирования
          if (fullItem.fileType === 'article') {
            // Проверяем наличие флага для редактирования
            const shouldEdit = fullItem.content === '' || 
                              sessionStorage.getItem(`edit_file_${fullItem.id}`) === 'true';
            
            if (shouldEdit) {
              console.log('Автоматически активируем режим редактирования содержимого для статьи:', fullItem.id);
              setTimeout(() => {
                setIsEditingContent(true);
                // Очищаем флаг из sessionStorage после использования
                sessionStorage.removeItem(`edit_file_${fullItem.id}`);
                window.scrollTo(0, 0); // <--- ДОБАВЛЕННАЯ СТРОКА: Убедимся, что прокрутка вверху после активации редактирования
              }, 100); // Небольшая задержка для более надежной активации
            }
          }
        } else {
          // Если запрос ещё актуален — используем локальный элемент как fallback
          if (selectItemLoadSeqRef.current === loadSeq && latestSelectRequestedItemIdRef.current === requestedId) {
            setSelectedItem(itemWithFixedParent);
          }
        }
      } catch (error) {
        // Не трогаем выбранный документ, если запрос уже устарел из-за нового клика.
        if (latestSelectRequestedItemIdRef.current !== itemWithFixedParent.id) {
          return;
        }
        setSelectedItem(itemWithFixedParent);
      }
    } else {
      setSelectedItem(itemWithFixedParent);
    }
  };

  // Преобразование данных из нашего формата в BoardState
  const convertToBoardState = (board: KanbanBoard): BoardState => {
    const cards: { [key: string]: any } = {};
    const columns = board.columns.map((column) => {
      const cardIds = column.cards.map((card) => {
        cards[card.id] = {
          id: card.id,
          title: card.title,
          description: card.description || '',
          priority: card.priority || 'medium',
          deadline: card.deadline,
          tags: card.tags || []
        };
        return card.id;
      });
      
      return {
        id: column.id,
        title: column.title,
        cardIds,
        isCompletedColumn: column.isCompletedColumn || false
      };
    });
    
    return {
      columns,
      cards,
      columnOrder: board.columns.map((col) => col.id),
      boardTitle: board.title || 'Канбан доска'
    };
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center h-full">
          <LoadingAnimation size="small" />
        </div>
      );
    }

    if (!selectedItem) {
      return (
        <div className="flex flex-col items-center justify-center h-full text-gray-500 dark:text-gray-400">
          <FileText className="w-16 h-16 mb-4" />
          <p>Выберите файл для просмотра</p>
        </div>
      );
    }

    // Добавляем логирование для отладки
    console.log('[Knowledge] renderContent вызван для selectedItem:', {
      id: selectedItem.id,
      name: selectedItem.name,
      isTemporary: selectedItem.id.startsWith('temp-'),
      itemType: selectedItem.itemType
    });

    return (
      <div className="space-y-6">
        {selectedItem.itemType !== 'folder' && !isChatOpen && (
          <div className="flex justify-center items-center mt-8 relative w-full px-4">
            {false && (
              <button
                onClick={() => {
                  setIsChatOpen(true);
                  // Сохраняем состояние открытого чата
                  if (selectedItem) {
                    saveChatState(selectedItem.id, true);
                  }
                }}
                className="flex items-center px-5 py-2 text-sm bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg shadow-md transform -translate-y-0.5 transition-all duration-200 font-medium"
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                Обсудить с ИИ
              </button>
            )}
            {selectedItem && !selectedItem.id.startsWith('temp-') && (
              <div className="absolute flex items-center space-x-1" style={{ right: '4%' }}>
                {/* Кнопка участников - показываем только для shared документов с больше чем одним участником */}
                {hasParticipants && (
                                      <button
                      onClick={() => setShowParticipantsModal(true)}
                      className="flex items-center px-3 py-2 text-sm text-gray-700 dark:text-white font-medium"
                      title={`Участники (isShared: ${selectedItem?.isShared}, participants: ${selectedItem?.participants?.length || 0})`}
                    >
                      <Users className="w-5 h-5 md:w-4 md:h-4 md:mr-2" />
                      <span className="hidden md:inline">
                        Участники
                      </span>
                    </button>
                )}
                
                {/* Кнопка поделиться */}
                <button
                  onClick={() => setShowSendMailDialog(true)}
                  className="flex items-center px-3 py-2 text-sm text-gray-700 dark:text-white font-medium"
                  title="Поделиться документом"
                >
                  <Share className="w-5 h-5 md:w-4 md:h-4 md:mr-2" />
                  <span className="hidden md:inline">Поделиться</span>
                </button>
              </div>
            )}
          </div>
        )}

        {isChatOpen && selectedItem.itemType !== 'folder' && (
          <KnowledgeChat
            isOpen={isChatOpen}
            onClose={() => {
              setIsChatOpen(false);
              setIsChatFullscreen(false);
              // Сохраняем состояние закрытого чата
              if (selectedItem) {
                saveChatState(selectedItem.id, false);
                // Очищаем фоновую сессию так как чат больше не нужен
                cleanupBackgroundSession(selectedItem.id);
              }
            }}
            content={selectedItem.content || ''}
            title={selectedItem.name}
            itemId={selectedItem.id}
            isFullscreen={isChatFullscreen}
            onToggleFullscreen={handleToggleChatFullscreen}
            backgroundChatService={getBackgroundChatService(selectedItem.id) || getPartialBackgroundChatService(selectedItem.id)}
            getCachedHistory={getCachedHistory}
          />
        )}

        {selectedItem.itemType === 'folder' ? (
          <div className="p-6 max-w-6xl mx-auto">
            <div className="prose dark:prose-invert max-w-none">
              <EditableTitle
                title={selectedItem.name}
                onUpdate={handleTitleUpdate}
                onInputChange={handleTitleInputChange}
                className="text-2xl font-bold text-gray-900 dark:text-white mb-4"
              />
              <p className="text-gray-600 dark:text-gray-400">
                Папка
              </p>
            </div>
          </div>
        ) : selectedItem.fileType === 'roadmap' ? (
          <div className="p-6 max-w-6xl mx-auto">
            <EditableTitle
              title={selectedItem.name}
              onUpdate={handleTitleUpdate}
              onInputChange={handleTitleInputChange}
              className="text-2xl font-bold text-gray-900 dark:text-white mb-4"
            />
            <GoalViewer
              markdown={selectedItem.content || ''}
              completedTasks={selectedItem.metadata?.completedTasks || []}
              onTaskToggle={handleTaskToggle}
              onMarkdownChange={async (newMarkdown) => {
                // Проверка на временный ID перед обновлением
                if (selectedItem.id.startsWith('temp-')) {
                  console.log('Предотвращено обновление roadmap с временным ID:', selectedItem.id);
                  return;
                }
                
                // Создаем обновленный элемент
                const updatedItem = {
                  ...selectedItem,
                  content: newMarkdown
                };
                
                // Оптимистично обновляем UI сразу
                setSelectedItem(updatedItem);
                
                // Обновляем элемент в структуре дерева
                setItems(prevItems => {
                  return updateItemContent(prevItems, selectedItem.id, newMarkdown);
                });
                
                // Отправляем запрос на сервер в фоне
                try {
                  await knowledgeApi.updateItem(selectedItem.id, updatedItem);
                } catch (error) {
                  
                }
              }}
            />
          </div>
        ) : selectedItem.fileType === 'chat' ? (
          <div className="flex-1 flex flex-col h-full bg-white dark:bg-gray-800">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <EditableTitle
                title={selectedItem.name}
                onUpdate={handleTitleUpdate}
                onInputChange={handleTitleInputChange}
                className="text-xl font-semibold text-gray-900 dark:text-white"
              />
            </div>
            <ChatHistory 
              messages={parseMessages(selectedItem.content || '')}
              currentUserId="user"
            />
          </div>
        ) : selectedItem.fileType === 'reels' ? (
          <div className="p-6 max-w-7xl mx-auto">
            <EditableTitle
              title={selectedItem.name}
              onUpdate={handleTitleUpdate}
              onInputChange={handleTitleInputChange}
              className="text-2xl font-bold text-gray-900 dark:text-white mb-4"
            />
            <ReelsList
              key={selectedItem.id}
              reels={parseReels(selectedItem.content)} 
              sourceId={selectedItem.id.startsWith('temp-') ? 
                      // Если ID временный, добавляем лог и передаем пустую строку вместо временного ID
                      (console.log('Передан временный ID в ReelsList:', selectedItem.id), '') : 
                      selectedItem.id}
              // Убираем onReelsDeleted чтобы предотвратить перерендер страницы
              items={items} // Передаем items через props вместо загрузки внутри компонента
              onPreventLoadItems={setPreventLoadItems} // Передаем функцию для блокировки загрузки items
              onUpdateContent={updateSelectedItemContent} // Передаем функцию для обновления содержимого
            />
          </div>
        ) : selectedItem.fileType === 'content-plan' ? (
          <div className="p-6 max-w-7xl mx-auto">
            <EditableTitle
              title={selectedItem.name}
              onUpdate={handleTitleUpdate}
              onInputChange={handleTitleInputChange}
              className="text-2xl font-bold text-gray-900 dark:text-white mb-4"
            />
            <ContentPlanTable 
              planData={contentPlanData || parseContentPlan(selectedItem.content)} 
              contentPlanId={selectedItem.id.startsWith('temp-') ? '' : selectedItem.id}
            />
          </div>
        ) : selectedItem.fileType === 'audience' ? ( // <-- Новая ветка
          <div className="p-6 max-w-7xl mx-auto">
            <EditableTitle
              title={selectedItem.name}
              onUpdate={handleTitleUpdate}
              onInputChange={handleTitleInputChange}
              className="text-2xl font-bold text-gray-900 dark:text-white mb-4"
            />
            {/* Замените <AudienceViewer ... /> на ваш реальный компонент */}
            {/* Убедитесь, что вы парсите JSON перед передачей: JSON.parse(selectedItem.content || '{}') */}
            {/* <AudienceViewer data={JSON.parse(selectedItem.content || '{}')} /> */} 
             {/* <p>Здесь будет отображение файла Audience (замените эту строку на ваш компонент)</p> */}
             {(() => {
               try {
                 const parsedData = JSON.parse(selectedItem.content || '{}');
                 return <AudienceAnalysisResultComponent data={parsedData} />; 
               } catch (error) {
                 return <div className="text-red-500">Ошибка отображения данных: неверный формат JSON.</div>;
               }
             })()}
          </div>
        ) : selectedItem.fileType === 'kanban' ? ( // <-- Новая ветка для канбан
          <div className="p-6 max-w-7xl mx-auto">
            <EditableTitle
              title={selectedItem.name}
              onUpdate={handleTitleUpdate}
              onInputChange={handleTitleInputChange}
              className="text-2xl font-bold text-gray-900 dark:text-white mb-6"
            />
            {(() => {
              try {
                // Парсим содержимое канбан документа
                const kanbanData: KanbanData = selectedItem.content ? JSON.parse(selectedItem.content) : { boards: [] };
                
                // Если нет досок, создаем одну по умолчанию
                if (!kanbanData.boards || kanbanData.boards.length === 0) {
                  kanbanData.boards = [{
                    id: `board-${Date.now()}`,
                    title: 'Основная доска',
                    columns: [
                      {
                        id: 'todo',
                        title: 'К выполнению',
                        cards: []
                      },
                      {
                        id: 'in-progress',
                        title: 'В работе',
                        cards: []
                      },
                      {
                        id: 'done',
                        title: 'Выполнено',
                        cards: []
                      }
                    ]
                  }];
                }

                // Функция для обновления канбан данных
                const updateKanbanData = (newData: KanbanData) => {
                  const updatedContent = JSON.stringify(newData);
                  
                  // Обновляем локальное состояние
                  setSelectedItem(prev => prev ? { ...prev, content: updatedContent } : null);
                  
                  // Обновляем в списке файлов
                  setItems(prevItems => {
                    return updateItemContent(prevItems, selectedItem.id, updatedContent);
                  });
                  
                  // Автосохранение (если не временный файл)
                  if (!selectedItem.id.startsWith('temp-')) {
                    // Используем debounce для предотвращения частых запросов
                    if (autoSaveTimer.current) {
                      clearTimeout(autoSaveTimer.current);
                    }
                    
                    autoSaveTimer.current = setTimeout(() => {
                      knowledgeApi.updateItem(selectedItem.id, {
                        ...selectedItem,
                        content: updatedContent
                      }).catch(error => {
                      });
                    }, 1000);
                  }
                };

                // Функция для добавления новой доски
                const addNewBoard = () => {
                  const newBoard = {
                    id: `board-${Date.now()}`,
                    title: `Доска ${kanbanData.boards.length + 1}`,
                    columns: [
                      {
                        id: 'todo',
                        title: 'К выполнению',
                        cards: []
                      },
                      {
                        id: 'in-progress',
                        title: 'В работе',
                        cards: []
                      },
                      {
                        id: 'done',
                        title: 'Выполнено',
                        cards: []
                      }
                    ]
                  };
                  
                  const newData = {
                    ...kanbanData,
                    boards: [...kanbanData.boards, newBoard]
                  };
                  
                  updateKanbanData(newData);
                };

                // Функция для обновления доски
                const updateBoard = (boardId: string, updatedBoard: Partial<KanbanBoard>) => {
                  const newData = {
                    ...kanbanData,
                    boards: kanbanData.boards.map((board: KanbanBoard) => 
                      board.id === boardId ? { ...board, ...updatedBoard } : board
                    )
                  };
                  
                  updateKanbanData(newData);
                };

                // Функция для удаления доски
                const deleteBoard = (boardId: string) => {
                  
                  if (kanbanData.boards.length <= 1) {
                    console.log('Cannot delete last board');
                    return; // Не удаляем последнюю доску
                  }
                  
                  const newData = {
                    ...kanbanData,
                    boards: kanbanData.boards.filter((board: KanbanBoard) => board.id !== boardId)
                  };
                  
                  updateKanbanData(newData);
                };

                // Функция для установки колонки "Выполнено"
                const setCompletedColumn = (boardId: string, columnId: string) => {
                    const boardToUpdate = kanbanData.boards.find(b => b.id === boardId);
                    if (!boardToUpdate) return;

                    const updatedColumns = boardToUpdate.columns.map(col => ({
                        ...col,
                        isCompletedColumn: col.id === columnId
                    }));

                    updateBoard(boardId, { columns: updatedColumns });
                };

                // Функция для удаления колонки
                const deleteColumn = (boardId: string, columnId: string) => {
                    const boardToUpdate = kanbanData.boards.find(b => b.id === boardId);
                    if (!boardToUpdate || boardToUpdate.columns.length <= 1) return;

                    const columnToDelete = boardToUpdate.columns.find(c => c.id === columnId);
                    if (!columnToDelete) return;

                    const cardIdsToRemove = columnToDelete.cards.map(c => c.id);
                    
                    const newColumns = boardToUpdate.columns.filter(c => c.id !== columnId);

                    // Это не очень эффективно, но для простоты сойдет
                    const newBoardContent = JSON.parse(selectedItem.content || '{}');
                    const boardIndex = newBoardContent.boards.findIndex((b: KanbanBoard) => b.id === boardId);
                    if(boardIndex !== -1) {
                        newBoardContent.boards[boardIndex].columns = newColumns;
                        const newContent = JSON.stringify(newBoardContent, null, 2);
                        const updatedItem = { ...selectedItem, content: newContent };
                        setSelectedItem(updatedItem);
                        updateKanbanData(newBoardContent);
                    }
                };

                // Функция для добавления колонки
                const addColumn = (boardId: string) => {
                     const boardToUpdate = kanbanData.boards.find(b => b.id === boardId);
                    if (!boardToUpdate) return;

                    const newColumn = {
                        id: `col-${Date.now()}`,
                        title: 'Новая колонка',
                        cards: []
                    };
                    
                    const updatedColumns = [...boardToUpdate.columns, newColumn];
                    updateBoard(boardId, { columns: updatedColumns });
                };

                return (
                  <div className="space-y-8">
                    {/* Кнопка добавления новой доски */}
                    <div className="flex justify-between items-center">
                      <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300">
                      </h3>
                      <button
                        onClick={addNewBoard}
                        className="text-blue-500 hover:text-blue-600 transition-colors p-2"
                        title="Добавить доску"
                      >
                        <Plus className="w-6 h-6" />
                      </button>
                    </div>

                    {/* Отображение всех досок */}
                    {kanbanData.boards.map((board: KanbanBoard, index: number) => (
                      <KanbanBoardStandalone
                        key={board.id}
                        boardState={convertToBoardState(board)}
                        boardId={board.id}
                        itemId={selectedItem.id}
                        onSetCompletedColumn={setCompletedColumn}
                        onDeleteColumn={deleteColumn}
                        onAddColumn={addColumn}
                        onDeleteBoard={deleteBoard}
                        canDeleteBoard={kanbanData.boards.length > 1}
                        onBoardChange={(boardId, newState) => {
                          // Преобразуем обратно в наш формат
                          const convertedColumns = newState.columns.map((column: KanbanColumn) => ({
                            id: column.id,
                            title: column.title,
                            isCompletedColumn: column.isCompletedColumn,
                            cards: column.cardIds.map((cardId: string) => {
                              const card = newState.cards[cardId];
                              return {
                                id: card.id,
                                title: card.title,
                                description: card.description,
                                priority: card.priority,
                                deadline: card.deadline,
                                tags: card.tags
                              };
                            })
                          }));
                          
                          updateBoard(boardId, { columns: convertedColumns, title: newState.boardTitle });
                        }}
                      />
                    ))}
                  </div>
                );
              } catch (error) {
                return (
                  <div className="text-center py-8">
                    <p className="text-red-500 mb-4">Ошибка загрузки канбан данных</p>
                    <button
                      onClick={() => {
                        // Создаем новые данные по умолчанию
                        const defaultData = {
                          boards: [{
                            id: `board-${Date.now()}`,
                            title: 'Основная доска',
                            columns: [
                              { id: 'todo', title: 'К выполнению', cards: [] },
                              { id: 'in-progress', title: 'В работе', cards: [] },
                              { id: 'done', title: 'Выполнено', cards: [] }
                            ]
                          }]
                        };
                        
                        const updatedContent = JSON.stringify(defaultData);
                        setSelectedItem(prev => prev ? { ...prev, content: updatedContent } : null);
                      }}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Создать новую доску
                    </button>
                  </div>
                );
              }
            })()}
          </div>
        ) : ( // <-- Существующая ветка else для остальных типов
          // НЕ показываем RichTextEditor для временных элементов
          selectedItem && !selectedItem.id.startsWith('temp-') ? (
            <div className="p-6 max-w-6xl mx-auto">
              <RichTextEditor
                key={selectedItem.id}
                content={isEditingContent ? editableContent : selectedItem.content || ''}
                isEditing={isEditingContent}
                onEdit={toggleEditMode}
                onSave={() => {
                  // Дополнительная проверка на временный ID перед сохранением
                  if (selectedItem.id.startsWith('temp-')) {
                    return;
                  }
                  
                  // Вызываем сохранение без обновления интерфейса
                  knowledgeApi.updateItem(selectedItem.id, {
                    ...selectedItem,
                    content: isEditingContent ? editableContent : selectedItem.content || ''
                  }).catch(error => {
                  });
                }}
                onChange={handleContentChange}
                title={selectedItem.name}
                withBackground={false}
                itemId={selectedItem.id} // Передаем ID всегда, включая временные
                format="html"
                fileType={selectedItem.fileType} // <--- ДОБАВЛЯЕМ ЯВНУЮ ПЕРЕДАЧУ fileType
                onUpdateTitle={handleTitleUpdate}
                onTitleInputChange={handleTitleInputChange}
                onIdChange={(oldId: string, newId: string) => {
                }}
              />
            </div>
          ) : (
            // Для временных элементов показываем сообщение о необходимости ввести название
            <div className="p-6 max-w-6xl mx-auto flex items-center justify-center">
              <div className="text-center text-gray-500 dark:text-gray-400">
                <h3 className="text-lg font-medium mb-2">Введите название файла</h3>
                <p className="text-sm">Сначала введите название файла в структуре слева, чтобы продолжить работу</p>
              </div>
            </div>
          )
        )}
      </div>
    );
  };

  // Обработчик изменения имени в поле редактирования
  const handleEditNameChange = (value: string) => {
    setEditName(value);
    
    // Немедленно обновляем имя в состоянии без отправки на сервер
    if (isEditing) {
      // Обновляем состояние элементов МГНОВЕННО
      setItems(prev => {
        const updateItemName = (items: KnowledgeItem[]): KnowledgeItem[] => {
          return items.map(item => {
            if (item.id === isEditing) {
              // Обновляем имя временного файла сразу в UI
              return {
                ...item,
                name: value
              };
            }
            if (item.children && item.children.length > 0) {
              return {
                ...item,
                children: updateItemName(item.children)
              };
            }
            return item;
          });
        };
        return updateItemName(prev);
      });
      
      // Если элемент выбран, обновляем его имя и в selectedItem МГНОВЕННО
      if (selectedItem?.id === isEditing) {
        setSelectedItem(prev => {
          if (prev) {
            return {
              ...prev,
              name: value
            };
          }
          return prev;
        });
      }
    }
  };

  // Добавим функцию для экспорта PDF
  const handleExportPDF = (item: KnowledgeItem) => {
    if (!item || !item.content) return;
    
    // Убедимся, что html2pdf доступен
    if (typeof window.html2pdf === 'undefined') {
      return;
    }
    
    const element = document.createElement('div');
    element.className = 'prose max-w-none p-8';
    element.style.color = '#000000';
    
    const styleElement = document.createElement('style');
    styleElement.textContent = `
      * {
        color: #000000 !important;
      }
      h1, h2, h3, h4, h5, h6 {
        color: #000000 !important;
        margin-bottom: 1em;
      }
      p {
        color: #000000 !important;
        margin-bottom: 0.5em;
      }
      a {
        color: #2563eb !important;
        text-decoration: underline;
      }
    `;
    element.appendChild(styleElement);
    
    const titleElement = document.createElement('h1');
    titleElement.style.fontSize = '24px';
    titleElement.style.fontWeight = 'bold';
    titleElement.style.marginBottom = '24px';
    titleElement.style.color = '#000000';
    titleElement.textContent = item.name || 'Документ';
    
    element.appendChild(titleElement);
    
    const contentDiv = document.createElement('div');
    contentDiv.innerHTML = item.content;
    element.appendChild(contentDiv);

    const opt = {
      margin: 10,
      filename: `${(item.name || 'document').toLowerCase().replace(/\s+/g, '-')}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
    };

    try {
      // @ts-ignore
      html2pdf().set(opt).from(element).save();
    } catch (error) {
      console.error('Ошибка при создании PDF:', error);
    }
    
    // Закрываем контекстное меню
    setContextMenu(null);
  };

  // Обработчик клика на элемент знания
  const handleKnowledgeItemClick = (knowledgeItem: KnowledgeItem) => {
    // Переключение состояния открытости чата
    setOpenChats((prev: KnowledgeItem['id'][]) => {
      const isOpen = prev.includes(knowledgeItem.id);
      if (isOpen) {
        // Закрываем чат
        const newOpenChats = prev.filter((id: string) => id !== knowledgeItem.id);
        return newOpenChats;
      } else {
        // Открываем чат
        const newOpenChats = [...prev, knowledgeItem.id];
        return newOpenChats;
      }
    });
  };

  // Обработчик изменения содержимого
  const handleContentChange = (newContent: string) => {
    console.log('[Knowledge] Получено обновление содержимого:', newContent.substring(0, 50) + '...');
    
    if (selectedItem?.id) {
      // Обновляем локальное состояние
      setEditableContent(newContent);
      
      // Обновляем элемент в списке файлов (сохраняем как есть в HTML)
      setItems(prevItems => {
        return updateItemContent(prevItems, selectedItem.id, newContent);
      });
      
      // Добавляем автоматическое сохранение для файлов с постоянным ID
      if (!selectedItem.id.startsWith('temp-') && selectedItem.content !== newContent) {
        // Используем debounce для предотвращения частых запросов
        if (autoSaveTimer.current) {
          clearTimeout(autoSaveTimer.current);
        }
        
        autoSaveTimer.current = setTimeout(() => {
          console.log('[Knowledge] Автоматическое сохранение содержимого для', selectedItem.id);
          const itemToSave = {
            ...selectedItem,
            content: newContent
          };
          
          // Отправляем запрос на сервер
          knowledgeApi.updateItem(selectedItem.id, itemToSave)
            .then(result => {
              console.log('[Knowledge] Успешное автосохранение содержимого для', selectedItem.id);
              // НЕ обновляем selectedItem чтобы избежать конфликтов с локальными изменениями
              // и предотвратить нежелательную навигацию через SignalR события
            })
            .catch(error => {
              console.error('[Knowledge] Ошибка автосохранения содержимого:', error);
            });
        }, 2000); // 2 секунды задержка перед сохранением
      }
    }
  };
  
  // Вспомогательная функция для обновления контента в дереве файлов
  const updateItemContent = (items: KnowledgeItem[], itemId: string, content: string): KnowledgeItem[] => {
    return items.map(item => {
      if (item.id === itemId) {
        // Обновляем контент, сохраняя все остальные свойства
        console.log(`[Knowledge] Обновляем контент в элементе ${itemId}:`, content.substring(0, 50) + '...');
        return {
          ...item,
          content
        };
      }
      
      // Если элемент имеет дочерние элементы, рекурсивно их обрабатываем
      if (item.children && item.children.length > 0) {
        return {
          ...item,
          children: updateItemContent(item.children, itemId, content)
        };
      }
      
      // Возвращаем без изменений, если это не нужный нам элемент
      return item;
    });
  };

  // Функция для рендеринга модальных окон через портал
  const renderPortal = (content: React.ReactNode) => {
    if (!modalPortalRoot) return null;
    return ReactDOM.createPortal(content, modalPortalRoot);
  };

  // Используем функции из хука useFileRename
  const handleTitleUpdate = handleTitleUpdateFromHook;
  const handleTitleInputChange = handleTitleInputChangeFromHook;

  // Добавляем обработчик для внутренних ссылок
  useEffect(() => {
    const handleLinkClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const link = target.closest('a');
      
      if (!link) return;
      
      const href = link.getAttribute('href');
      if (!href) return;
      
      // Проверяем, является ли ссылка внутренней
      if (href.startsWith('internal://')) {
        e.preventDefault();
        
        // Извлекаем ID документа из ссылки
        const parts = href.replace('internal://', '').split('/');
        const documentId = parts[0];
        
        if (documentId) {
          // Переходим к документу
          navigate(`/knowledge/${documentId}`);
        }
      }
    };
    
    // Добавляем обработчик на клики
    document.addEventListener('click', handleLinkClick);
    
    return () => {
      document.removeEventListener('click', handleLinkClick);
    };
  }, [navigate]);

  // Добавляем стили для внутренних ссылок
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      a[href^="internal://"] {
        color: #4e4ae7 !important; /* Синий цвет для внутренних ссылок */
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Добавим обработчик для отслеживания состояния меню
  const handleMenuStateChange = useCallback((isOpen: boolean) => {
    console.log('Knowledge: обработка изменения состояния меню:', {isOpen});
    
    // Проверяем, чтобы избежать рендеринга с невалидным состоянием
    if (typeof isOpen !== 'boolean') {
      console.error('handleMenuStateChange получил невалидное значение:', isOpen);
      return;
    }
    
    setIsMenuOpenForContentShift(isOpen);
    
    // Устанавливаем CSS переменную для отступа контента на desktop
    const isDesktop = window.innerWidth >= 1024;
    if (isDesktop) {
      // document.documentElement.style.setProperty(
      //   '--knowledge-panel-width', 
      //   isOpen ? '300px' : '0px'
      // );
    } else {
      // document.documentElement.style.setProperty('--knowledge-panel-width', '0px');
    }
  }, []); // Пустой массив зависимостей, так как функция не зависит от пропсов или состояния

  // Функция для обновления содержимого выбранного файла после операций с рилсами
  const updateSelectedItemContent = async () => {
    if (!selectedItem || selectedItem.id.startsWith('temp-')) {
      return;
    }
    
    try {
      await knowledgeApi.updateItem(selectedItem.id, { ...selectedItem, content: editableContent });
      // Обновление прошло успешно, кэш обновится через SignalR
    } catch (error) {
      console.error('[Knowledge] Ошибка при обновлении содержимого файла:', error);
    }
  };

  // Обработчик успешного шаринга документа
  const handleDocumentShared = useCallback(async (knowledgeItemId: string) => {
    console.log(`[Knowledge] 🔄 Начинаем обработку шаринга для документа ${knowledgeItemId}`);
    
    // Проверяем, есть ли локальные изменения (временные элементы)
    const hasLocalChanges = items.some(item => item.id.startsWith('temp-'));
    if (hasLocalChanges) {
      console.log('[Knowledge] Игнорируем обновление шаринга при наличии локальных изменений');
      return;
    }
    
    // Обновляем локальное состояние items
    setItems(prevItems => {
      console.log(`[Knowledge] 📝 Обновляем локальное состояние items для ${knowledgeItemId}`);
      const updateItemSharedStatus = (items: KnowledgeItem[]): KnowledgeItem[] => {
        return items.map(item => {
          if (item.id === knowledgeItemId) {
            console.log(`[Knowledge] ✅ Найден элемент ${item.name}, устанавливаем isShared = true`);
            return { 
              ...item, 
              isShared: true,
              // Временно устанавливаем participantCount в 2, пока не получим данные с сервера
              participantCount: Math.max(item.participantCount || 1, 2)
            };
          }
          if (item.children) {
            return { ...item, children: updateItemSharedStatus(item.children) };
          }
          return item;
        });
      };
      const updatedItems = updateItemSharedStatus(prevItems);
      console.log(`[Knowledge] 📋 Состояние items обновлено для ${knowledgeItemId}`);
      return updatedItems;
    });

    // Также обновляем selectedItem если это тот же документ
    setSelectedItem(prevSelected => {
      if (prevSelected && prevSelected.id === knowledgeItemId) {
        console.log(`[Knowledge] 🎯 Обновляем selectedItem для ${knowledgeItemId}: isShared = true`);
        return { 
          ...prevSelected, 
          isShared: true,
          // Временно устанавливаем participantCount в 2, пока не получим данные с сервера
          participantCount: Math.max(prevSelected.participantCount || 1, 2)
        };
      }
      return prevSelected;
    });

    // ВАЖНО: Принудительно обновляем данные с сервера, чтобы кэш не блокировал изменения
    try {
      console.log(`[Knowledge] 🌐 Запрашиваем свежие данные с сервера для ${knowledgeItemId}...`);
      
      // Перезагружаем конкретный документ с сервера с принудительным обновлением
      const updatedDocument = await knowledgeApi.getFile(knowledgeItemId, true);
      
      console.log(`[Knowledge] 📥 Получены данные с сервера:`, {
        id: updatedDocument.id,
        name: updatedDocument.name,
        isShared: updatedDocument.isShared,
        itemType: updatedDocument.itemType
      });
      
      // Обновляем selectedItem актуальными данными с сервера
      if (selectedItem && selectedItem.id === knowledgeItemId) {
        console.log(`[Knowledge] 🔄 Заменяем selectedItem данными с сервера`);
        setSelectedItem(updatedDocument);
      }
      
      // Обновляем items с данными с сервера
      setItems(prevItems => {
        console.log(`[Knowledge] 🔄 Заменяем элемент в items данными с сервера`);
        const updateItemWithServerData = (items: KnowledgeItem[]): KnowledgeItem[] => {
          return items.map(item => {
            if (item.id === knowledgeItemId) {
              console.log(`[Knowledge] 🔄 Заменяем ${item.name} полными данными с сервера`);
              return { 
                ...item, 
                isShared: updatedDocument.isShared,
                participantCount: updatedDocument.participantCount,
                participants: updatedDocument.participants,
                isAuthor: updatedDocument.isAuthor
              };
            }
            if (item.children) {
              return { ...item, children: updateItemWithServerData(item.children) };
            }
            return item;
          });
        };
        return updateItemWithServerData(prevItems);
      });
      
      console.log(`[Knowledge] ✅ Документ ${knowledgeItemId} обновлен с сервера. isShared: ${updatedDocument.isShared}`);
      
      // Также обновляем структуру файлов из React Query кэша
      console.log(`[Knowledge] 🔄 Обновляем структуру файлов через refreshKnowledgeTree...`);
      refreshKnowledgeTree();
      
      console.log(`[Knowledge] 🎉 Обработка шаринга завершена для ${knowledgeItemId}`);
    } catch (error) {
      console.error(`[Knowledge] ❌ Ошибка при обновлении документа ${knowledgeItemId} с сервера:`, error);
    }
  }, [selectedItem, items]);

  // Обработчик SignalR уведомлений об обновлении открытого документа
  useEffect(() => {
    const handleDocumentUpdate = (event: CustomEvent) => {
      const { documentId, updates, editorUsername } = event.detail;
      console.log('[Knowledge] SignalR обновление документа:', documentId, 'updates:', updates, 'editorUsername:', editorUsername);
      
      // Проверяем, не является ли это временным элементом
      if (documentId.startsWith('temp-')) {
        console.log('[Knowledge] Игнорируем SignalR обновление для временного элемента:', documentId);
        return;
      }
      
      // Проверяем, есть ли у нас локальные изменения (временные элементы)
      const hasLocalChanges = items.some(item => item.id.startsWith('temp-'));
      if (hasLocalChanges) {
        console.log('[Knowledge] Игнорируем SignalR обновления при наличии локальных изменений');
        return;
      }
      
      // Обновляем selectedItem, если он совпадает с обновляемым документом
      setSelectedItem(prev => {
        if (!prev || prev.id !== documentId) {
          console.log('[Knowledge] selectedItem не совпадает с обновляемым документом');
          return prev;
        }
        
        console.log('[Knowledge] Обновляем selectedItem с данными от SignalR');
        const updatedItem = { ...prev };
        
        if (updates.name !== undefined) {
          updatedItem.name = updates.name;
        }
        
        if (updates.content !== undefined) {
          updatedItem.content = updates.content;
          
          // Инвалидируем кэш контент-плана для перезагрузки данных
          console.log('[Knowledge] Инвалидируем кэш контент-плана для обновления данных');
          queryClient.invalidateQueries({
            queryKey: ['content-plan', documentId]
          });
        }
        
        console.log(`[Knowledge] ✅ selectedItem обновлен с данными от ${updates.editorUsername}`);
        return updatedItem;
      });
    };
    
    // Подписываемся на события обновления документов
    window.addEventListener('knowledgeDocumentUpdated', handleDocumentUpdate as EventListener);
    
    return () => {
      window.removeEventListener('knowledgeDocumentUpdated', handleDocumentUpdate as EventListener);
    };
  }, [items]);

  // Очистка фоновых сессий чата при размонтировании
  useEffect(() => {
    return () => {
      cleanupAllBackgroundSessions();
    };
  }, [cleanupAllBackgroundSessions]);

  // Очистка таймеров при размонтировании
  useEffect(() => {
    return () => {
      if (autoSaveTimer.current) {
        clearTimeout(autoSaveTimer.current);
      }
      cleanupFileRename();
    };
  }, [cleanupFileRename]);

  // Отслеживаем изменения в items для отладки
  useEffect(() => {
    console.log('[Knowledge] Состояние items изменилось, количество элементов:', items.length);
    
    // Проверяем наличие временных элементов
    const tempItems = items.filter(item => item.id.startsWith('temp-'));
    if (tempItems.length > 0) {
      console.log('[Knowledge] Найдены временные элементы:', tempItems.map(item => ({ id: item.id, name: item.name })));
    } else {
      console.log('[Knowledge] Временные элементы отсутствуют');
    }
    
    // Логируем все элементы для отладки
    console.log('[Knowledge] Все элементы items:', items.map(item => ({ id: item.id, name: item.name, type: item.itemType })));
  }, [items]);

  // Представление страницы
  if (isLoading) {
    return <LoadingAnimation withNavigation size="large" />;
  }

  return (
    <div className="min-h-screen h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 dark:from-gray-900 dark:via-slate-800 dark:to-gray-900 transition-colors duration-200 flex flex-col mobile-content-padding">
      <div className="hidden md:block">
        {/* Заменяем NavigationMenu на MemoizedNavigationMenu */}
        <MemoizedNavigationMenu 
          onMenuStateChange={handleMenuStateChange} 
          isMenuActuallyOpen={isMenuOpenForContentShift} 
        />
      </div>
      
      {/* Кнопка открытия меню для мобильных и планшетов */}
      {!isMenuOpenForContentShift && !isMobileOverlayOpen && (
        <div className="lg:hidden fixed top-4 left-4 z-50">
          <button
            onClick={() => {
              // На мобильных (< 768px) показываем мобильный оверлей
              if (window.innerWidth < 768) {
                setIsMobileOverlayOpen(true);
              } else {
                // На планшетах показываем выезжающее меню
                handleMenuStateChange(true);
              }
            }}
            className="p-3 bg-white dark:bg-gray-800 rounded-full shadow-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <Menu className="w-5 h-5 text-gray-600 dark:text-gray-300" />
          </button>
        </div>
      )}
      
      {/* Основной контейнер для файловой структуры и контента */}
      <div className="flex-1 flex overflow-hidden">
        {/* KnowledgeFileStructure скрыт для мобильных устройств (< 768px) */}
        <div className="hidden md:flex">
          <KnowledgeFileStructure
          items={items}
          selectedItem={selectedItem}
          expandedFolders={expandedFolders}
          isEditing={isEditing}
          editName={editName}
          onToggleFolder={toggleFolder}
          onMenuStateChange={handleMenuStateChange}
          isMenuVisible={isMenuOpenForContentShift}
          onSelectItem={handleSelectItem}
          onContextMenu={handleContextMenu}
          onEditNameChange={handleEditNameChange}
          onEditSave={handleSaveEdit}
          onAddFile={() => {
            setNewArticleParentId(undefined);
            setShowNewArticleDialog(true);
          }}
          onAddFolder={() => addNewFolder()}
          movingItems={movingItems}
        />
        </div>

        <motion.div 
          className="flex-1 bg-white dark:bg-gray-900 overflow-y-auto mobile-content-padding w-full"
          onPanEnd={handleSwipeRight}
        >
          {renderContent()}
          
          {/* Индикация процесса перемещения */}
          {movingItems.size > 0 && (
            <div className="fixed bottom-4 right-4 z-50">
              <div className="bg-blue-500 text-white px-4 py-2 rounded-lg shadow-lg flex items-center space-x-2">
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                <span>Перемещение файлов...</span>
              </div>
            </div>
          )}
          
          {/* Отображение ошибок перемещения */}
          {moveError && (
            <div className="fixed bottom-4 left-4 z-50 max-w-md">
              <div className="bg-red-500 text-white px-4 py-3 rounded-lg shadow-lg">
                <div className="flex items-start space-x-2">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium">Ошибка перемещения</p>
                    <p className="text-sm opacity-90 mt-1">{moveError}</p>
                  </div>
                  <button
                    onClick={() => setMoveError(null)}
                    className="flex-shrink-0 ml-2 text-white hover:text-gray-200"
                  >
                    <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          )}
        </motion.div>
      </div>

      {/* Рендерим модальные окна через портал */}
      {modalPortalRoot && showNewArticleDialog && (
        renderPortal(
          <NewArticleDialog
            isOpen={showNewArticleDialog}
            onClose={() => setShowNewArticleDialog(false)}
            onSelect={(type, parentId) => handleNewArticle(type, parentId || newArticleParentId)}
            parentId={newArticleParentId}
          />
        )
      )}

      {modalPortalRoot && moveDialogItem !== null && (
        renderPortal(
          <MoveDialog
            isOpen={moveDialogItem !== null}
            onClose={() => setMoveDialogItem(null)}
            onMove={handleMoveItem}
            items={items}
            currentItem={moveDialogItem!}
          />
        )
      )}

      {modalPortalRoot && contextMenu && (
        renderPortal(
          <AnimatePresence>
            {contextMenu && (
              <div
                style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, overflow: 'hidden', touchAction: 'none', pointerEvents: 'none', zIndex: 9998 }}
                onWheel={(e) => e.preventDefault()}
                onTouchMove={(e) => e.preventDefault()}
              >
                <motion.div
                  ref={contextMenuRef}
                  initial={{ opacity: 0, scale: 0.95, y: 5 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: 5 }}
                  transition={{ duration: 0.15, ease: "easeOut" }}
                  className="fixed bg-white dark:bg-gray-800 rounded-xl shadow-lg py-2 z-[9999] border border-gray-200 dark:border-gray-600 ring-1 ring-black/5 dark:ring-white/10 backdrop-blur-sm"
                  style={{ 
                    left: contextMenu.x, 
                    top: contextMenu.y,
                    pointerEvents: 'auto'
                  }}
                >
                  {/* Кнопки создания файла и папки только для папок */}
                  {contextMenu.item.itemType === 'folder' && (
                    <>
                      <button
                        onClick={() => {
                          setNewArticleParentId(contextMenu.item.id);
                          setShowNewArticleDialog(true);
                          setContextMenu(null);
                        }}
                        className="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 flex items-center text-gray-900 dark:text-white transition-all"
                      >
                        <FileText className="w-4 h-4 mr-2 text-blue-500" />
                        Новый файл
                      </button>
                      <button
                        onClick={() => {
                          addNewFolder(contextMenu.item.id);
                          setContextMenu(null);
                        }}
                        className="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 flex items-center text-gray-900 dark:text-white transition-all"
                      >
                        <FolderPlus className="w-4 h-4 mr-2 text-blue-500" />
                        Новая папка
                      </button>
                    </>
                  )}
                  <button
                    onClick={() => handleEdit(contextMenu.item)}
                    className="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 flex items-center text-gray-900 dark:text-white transition-all"
                  >
                    <Edit2 className="w-4 h-4 mr-2 text-blue-500" />
                    Переименовать
                  </button>
                  <button
                    onClick={() => handleMove(contextMenu.item)}
                    className="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 flex items-center text-gray-900 dark:text-white transition-all"
                  >
                    <Move className="w-4 h-4 mr-2 text-blue-500" />
                    Переместить
                  </button>
                  
                  {/* Кнопка "Скачать PDF" только для типа article */}
                  {contextMenu.item.fileType === 'article' && (
                    <button
                      onClick={() => handleExportPDF(contextMenu.item)}
                      className="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 flex items-center text-gray-900 dark:text-white transition-all"
                    >
                      <FileDown className="w-4 h-4 mr-2 text-blue-500" />
                      Скачать PDF
                    </button>
                  )}
                  

                  
                  <div className="h-px bg-gray-200 dark:bg-gray-600 my-1" />
                  <button
                    onClick={() => handleDelete(contextMenu.item)}
                    className="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 flex items-center text-red-500 transition-all"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Удалить
                  </button>
                </motion.div>
              </div>
            )}
          </AnimatePresence>
        )
      )}

      {modalPortalRoot && showDeleteDialog && itemToDelete && (
        renderPortal(
          <div 
            className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[9999]" 
            style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, overflow: 'hidden' }}
            onWheel={(e) => e.preventDefault()}
            onTouchMove={(e) => e.preventDefault()}
          >
            <motion.div
              ref={deleteDialogRef}
              initial={{ scale: 0.95, opacity: 0, y: 10 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 10 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
              className="bg-white dark:bg-gray-800 rounded-xl p-6 max-w-sm w-full mx-4 shadow-xl border border-gray-200 dark:border-gray-600 ring-1 ring-black/5 dark:ring-white/10"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  confirmDelete();
                }
              }}
              tabIndex={0}
              autoFocus
            >
              <div className="flex items-center mb-4 text-red-500">
                <Trash2 className="w-6 h-6 mr-2" />
                <h3 className="text-lg font-semibold">Подтверждение удаления</h3>
              </div>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                Вы уверены, что хотите удалить "{itemToDelete.name}"?
                {(itemToDelete.itemType === 'folder' || (itemToDelete.children && itemToDelete.children.length > 0)) && (
                  <span className="block mt-2 text-red-500 text-sm">
                    Внимание: все файлы внутри {itemToDelete.itemType === 'folder' ? 'папки' : 'файла'} также будут удалены!
                  </span>
                )}
              </p>
              <div className="flex justify-end space-x-4">
                <button
                  onClick={() => {
                    setShowDeleteDialog(false);
                    setItemToDelete(null);
                  }}
                  className="px-4 py-2 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded-lg transition-all"
                >
                  Отмена
                </button>
                <button
                  onClick={confirmDelete}
                  className="px-4 py-2 bg-red-500/90 hover:bg-red-500 text-white rounded-lg shadow-sm hover:shadow transition-all"
                >
                  Удалить
                </button>
              </div>
            </motion.div>
          </div>
        )
      )}

      {/* SendMailDialog */}
      {selectedItem && showSendMailDialog && (
        <SendMailDialog
          isOpen={showSendMailDialog}
          onClose={() => setShowSendMailDialog(false)}
          knowledgeItemId={selectedItem.id}
          documentTitle={selectedItem.name}
          documentType={selectedItem.fileType}
          onDocumentShared={handleDocumentShared}
        />
      )}

      {/* DocumentParticipantsModal */}
      {selectedItem && showParticipantsModal && (
        <DocumentParticipantsModal
          isOpen={showParticipantsModal}
          onClose={() => setShowParticipantsModal(false)}
          knowledgeItemId={selectedItem.id}
          documentTitle={selectedItem.name}
          knowledgeItem={selectedItem}
        />
      )}

      {/* Mobile Knowledge Overlay */}
      <MobileKnowledgeOverlay
        isOpen={isMobileOverlayOpen}
        onClose={() => setIsMobileOverlayOpen(false)}
        items={items}
        selectedItem={selectedItem}
        expandedFolders={expandedFolders}
        isEditing={isEditing}
        editName={editName}
        onToggleFolder={toggleFolder}
        onSelectItem={handleSelectItem}
        onEditNameChange={handleEditNameChange}
        onEditSave={handleSaveEdit}
        onAddFile={() => {
          setNewArticleParentId(undefined);
          setShowNewArticleDialog(true);
        }}
        onAddFolder={() => addNewFolder()}
        onContextMenu={handleContextMenu}
        onCloseContextMenu={() => setContextMenu(null)}
      />
    </div>
  );
});

export default Knowledge;
